import React, { useState, useEffect, createContext, useContext, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams, useParams, Navigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  Camera,
  Menu, X, Calculator, LayoutDashboard, FileText, Bot, Phone, MessageSquare,
  Share2, TrendingUp, TrendingDown, Users, Building2, Clock, ChevronRight, ChevronDown,
  CheckCircle, ArrowRight, Utensils, Send, Sparkles, PoundSterling,
  Package, Calendar, BarChart3, LogOut, Award, Target, Plus, Edit, Trash2,
  AlertTriangle, UserCheck, ChefHat, Download, Mail, GitCompare, Copy,
  PartyPopper, Settings, Smartphone, CreditCard, ShoppingCart, MapPin,
  Sprout, Banknote, ShieldCheck, Wrench, Boxes, Workflow, Brain, Shield, Landmark,
  ClipboardCheck, Truck, RefreshCw
} from 'lucide-react';
import { shouldShowMobileApp, nativeStartPath, POST_LOGIN_ROUTE } from './mobileRouting';
import { ExperienceGuard, ExperienceProvider, apiAllowed, fetchExperience, landingFor, useExperience } from './experience/experience';
import LicenceBanner from './components/LicenceBanner';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import PpdsLabel, { usePpdsNavEnabled } from './components/PpdsLabel';
import MobileApp from './mobile/MobileApp';
import { isUnbuiltSurface, surfaceEnabled } from './featureFlags';
import { registerServiceWorker } from './utils/serviceWorkerRegistration';
import { apiErrorMessage } from './utils/apiError';
import { classifyLoginResponse } from './auth/loginResponse';
import SetYourPassword from './auth/SetYourPassword';
import { authHeaders, ensureOrgContext } from './utils/authHeaders';
import { NotFoundPage, AccessDeniedPage } from './components/RouteStates';

import { useDeviceScope, scopeKey } from './capture/deviceScope';
import { purgeReceivingDeviceState } from './receiving/offlineQueue';
import { useDecisionsNavEnabled } from './components/DecisionsShared';
import { useCkNavEnabled } from './components/CkOperationsShared';
import { useMarketplaceNavEnabled } from './components/MarketplaceShared';
import { useControlsNavEnabled } from './components/ControlsShared';
import { clearWorkforceRetryState } from './components/workforceExchangeQueue';
import { clearOperandiRetryState } from './components/operandiRetrySession';
import { logoutAllDevicesAction } from './authLogoutAll';
import { signOutOnServer } from './auth/signOutOnServer';
import IdleLock from './auth/IdleLock';
import { useWorkforceNavEnabled } from './components/WorkforceShared';
import { useForecastNavEnabled } from './components/DemandForecast';
import { useSupplierPriceListsNavEnabled } from './components/SupplierPriceListsShared';
import { useVisionNavEnabled } from './components/VisualSafetyShared';
import useMediaQuery from './utils/useMediaQuery';
import MobileViewOffer from './components/MobileViewOffer';
import { BrandLockup, BrandMark, PoweredBy, useBrand, useLabel } from './brand/BrandProvider';
import ReleaseStamp from './ReleaseStamp';
// Route modules load on demand (A16). Everything above is eager: the app shell,
// the sign-in page, and the public marketing pages a first-time visitor lands on.
// One Suspense boundary in App wraps the routes.
const EventsListPage = lazy(() => import('./EventPlanning').then((m) => ({ default: m.EventsListPage })));
const EventDetailPage = lazy(() => import('./EventPlanning').then((m) => ({ default: m.EventDetailPage })));
const MembershipPage = lazy(() => import('./Membership').then((m) => ({ default: m.MembershipPage })));
const AcceptInvitePage = lazy(() => import('./auth/AcceptInvite'));
const OnboardingWizard = lazy(() => import('./OnboardingWizard').then((m) => ({ default: m.OnboardingWizard })));
const PersonaOnboarding = lazy(() => import('./components/PersonaOnboarding'));
const RoomBooking = lazy(() => import('./RoomBooking'));
const InventoryManagement = lazy(() => import('./InventoryManagement'));
const CkOperations = lazy(() => import('./components/CkOperations'));
const OperandiHumanContext = lazy(() => import('./components/OperandiHumanContext'));
const TemperatureSettings = lazy(() => import('./receiving/TemperatureSettings'));
const InboundQueue = lazy(() => import('./receiving/InboundQueue'));
const OperatorInbox = lazy(() => import('./receiving/OperatorInbox'));
const ReceiptWorkspace = lazy(() => import('./receiving/ReceiptWorkspace'));
const ManagerReview = lazy(() => import('./receiving/ManagerReview'));
const Putaway = lazy(() => import('./receiving/Putaway'));
const Claims = lazy(() => import('./receiving/Claims'));
const SyncCenter = lazy(() => import('./receiving/SyncCenter'));
const CapturePage = lazy(() => import('./capture/CapturePage'));
const ScanGatesPage = lazy(() => import('./scan-admin/ScanAdmin').then((m) => ({ default: m.ScanGatesPage })));
const LotLineagePage = lazy(() => import('./scan-admin/ScanAdmin').then((m) => ({ default: m.LotLineagePage })));
const NotificationBell = lazy(() => import('./components/NotificationBell'));
const UpdateBanner = lazy(() => import('./components/UpdateBanner'));
const Waste = lazy(() => import('./components/Waste'));
const DecisionsReview = lazy(() => import('./components/DecisionsReview'));
const MarketplaceBuyer = lazy(() => import('./components/MarketplaceBuyer'));
const MarketplaceOperator = lazy(() => import('./components/MarketplaceOperator'));
const Controls = lazy(() => import('./components/Controls'));
const Workforce = lazy(() => import('./components/Workforce'));
const DemandForecast = lazy(() => import('./components/DemandForecast'));
const SupplierPriceLists = lazy(() => import('./components/SupplierPriceLists'));
const DeliveryManagement = lazy(() => import('./DeliveryManagement'));
const SafetyHealth = lazy(() => import('./SafetyHealth'));
const VisualSafety = lazy(() => import('./components/VisualSafety'));
const ContractorJobs = lazy(() => import('./components/ContractorJobs').then((m) => ({ default: m.ContractorJobs })));
const ContractorJobPortal = lazy(() => import('./components/ContractorJobs').then((m) => ({ default: m.ContractorJobPortal })));
const EnhancedEventPlanning = lazy(() => import('./EnhancedEventPlanning'));
const DirectConnect = lazy(() => import('./DirectConnect'));
const ROIDashboard = lazy(() => import('./ROIDashboard'));
const Billing = lazy(() => import('./Billing'));
const LocationSwitcher = lazy(() => import('./LocationSwitcher'));
const OrganizationSwitcher = lazy(() => import('./OrganizationSwitcher'));
const MenuConfiguration = lazy(() => import('./MenuConfiguration'));
const MenuItemManagement = lazy(() => import('./MenuItemManagement'));
const Documents = lazy(() => import('./Documents'));
const Xero = lazy(() => import('./Xero'));
const CRM = lazy(() => import('./CRM'));
const HRM = lazy(() => import('./HRM'));
const Procurement = lazy(() => import('./Procurement'));
const Forecast = lazy(() => import('./Forecast'));
const DataRoom = lazy(() => import('./DataRoom'));
const VoiceConcierge = lazy(() => import('./VoiceConcierge'));
const IntegrationsSettings = lazy(() => import('./IntegrationsSettings'));
const AiSettingsPage = lazy(() => import('./aisettings/AiSettingsPage'));
const DeputySettingsRedirect = lazy(() => import('./components/DeputySettingsRedirect'));
const RecipeManager = lazy(() => import('./RecipeManager'));
const Vendors = lazy(() => import('./Vendors'));
const N8NConfiguration = lazy(() => import('./N8NConfiguration'));
const PosOrderEntry = lazy(() => import('./PosOrderEntry'));
const TableManagement = lazy(() => import('./TableManagement'));
const AlertsCockpit = lazy(() => import('./components/AlertsCockpit'));
const CostIntelligence = lazy(() => import('./components/CostIntelligence'));
const PriceImpactPage = lazy(() => import('./pricing/PriceImpactPage'));
const ReviewQueue = lazy(() => import('./ReviewQueue'));
const InvoicesPage = lazy(() => import('./invoices/InvoicesPage'));
const ChefConfirm = lazy(() => import('./ChefConfirm'));
const AccessMatrix = lazy(() => import('./AccessMatrix'));
const TrialSignup = lazy(() => import('./TrialSignup'));
const FirstRunWizard = lazy(() => import('./FirstRunWizard'));
const KdsView = lazy(() => import('./components/KdsView'));
const CashierClose = lazy(() => import('./components/CashierClose'));
const GuestRSVP = lazy(() => import('./components/GuestRSVP'));
const GuestRoomBooking = lazy(() => import('./components/GuestRoomBooking'));
const SuperAdminConsole = lazy(() => import('./components/SuperAdminConsole'));
const MfaCodeStep = lazy(() => import('./components/MfaCodeStep'));
const MfaEnrol = lazy(() => import('./components/MfaEnrol'));
const SecuritySettings = lazy(() => import('./SecuritySettings'));
const SetupPage = lazy(() => import('./setup/SetupPage'));
const CompliancePage = lazy(() => import('./compliance/CompliancePage'));
const ImportDataPage = lazy(() => import('./imports/ImportDataPage'));
const FeedbackButton = lazy(() => import('./components/FeedbackButton'));
const OwnerQueue = lazy(() => import('./components/OwnerQueue'));
const GroupView = lazy(() => import('./components/GroupView'));
const ReplenishmentProposals = lazy(() => import('./components/ReplenishmentProposals'));
const MissedChecks = lazy(() => import('./components/MissedChecks'));
const ScanStationPage = lazy(() => import('./scan/ScanStationPage'));

if ('serviceWorker' in navigator) {
  registerServiceWorker();
}

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Active department is plain component state in the scope-bar owners (Home,
// ImplementationsManager) but the Navbar lives outside that tree and needs it
// to narrow nav visibility per department. Broadcast via localStorage + a
// same-tab custom event (storage events don't fire in the tab that wrote them).
const ACTIVE_DEPARTMENT_KEY = 'active_department';
const DEPARTMENT_CHANGED_EVENT = 'fi-department-changed';

const broadcastActiveDepartment = (dept) => {
  try {
    if (dept) localStorage.setItem(ACTIVE_DEPARTMENT_KEY, dept);
    else localStorage.removeItem(ACTIVE_DEPARTMENT_KEY);
  } catch (e) { /* localStorage unavailable */ }
  window.dispatchEvent(new CustomEvent(DEPARTMENT_CHANGED_EVENT, { detail: dept }));
};

const readActiveDepartment = () => {
  try { return localStorage.getItem(ACTIVE_DEPARTMENT_KEY) || ''; } catch (e) { return ''; }
};

// Mirrors backend module_gating.module_allowed (server.py's
// enforce_module_gating is the authoritative enforcement; this is UX only).
// Department narrowing only applies to the keys server.py's
// MODULE_ROUTE_PREFIXES actually gates — every other nav key (org-level
// toggles like 'safety'/'room-booking' with no per-department route
// enforcement) must keep the old "shown unless org-disabled" behavior.
const DEPARTMENT_GATEABLE_MODULES = ['events', 'inventory', 'scheduling', 'implementations', 'proposals', 'calculator', 'directconnect'];

const moduleAllowedClient = (moduleKey, modulesEnabled, departmentGrants, department) => {
  if ((modulesEnabled || {})[moduleKey] === false) return false;
  if (
    department &&
    DEPARTMENT_GATEABLE_MODULES.includes(moduleKey) &&
    departmentGrants &&
    Object.prototype.hasOwnProperty.call(departmentGrants, department)
  ) {
    return (departmentGrants[department] || []).includes(moduleKey);
  }
  return true;
};

/**
 * One shared Suspense fallback for every lazily loaded route (A16). Kept small and
 * eager: it renders before any route chunk has arrived.
 */
export const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-[40vh]" data-testid="route-fallback">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
  </div>
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        // Self-heal a session that has a valid token but no canonical org id
        // (e.g. one created before the org-key consolidation). Resolves from
        // the server, so it never reintroduces a stale org id. No-op once the
        // canonical key is present.
        await ensureOrgContext(token);
      } else {
        logout();
      }
    } catch (err) {
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    const step = classifyLoginResponse(data);
    if (step.kind === 'mfa') return { mfaRequired: true, mfaToken: step.mfaToken };
    if (step.kind === 'enrol') return { enrolmentRequired: true, mfaToken: step.mfaToken };
    if (step.kind === 'password_change') return { passwordChangeRequired: true, changeToken: step.changeToken };
    return completeLogin(data);
  };

  // Post-login storage shared by password login and two-factor verify; refuses anything without a real token.
  const completeLogin = async (data) => {
    if (classifyLoginResponse(data).kind !== 'session') throw new Error('Sign-in did not complete. Please try again.');
    localStorage.setItem('token', data.token);
    localStorage.setItem('auth_token', data.token);  // mobile shell SSO
    // Cache every org the user belongs to. An invited team member has TWO
    // memberships — the personal org self-registration created for them, and the
    // org they were invited into — so a single cached id stranded them in an
    // empty tenant. The switcher reads this list; it is not a display detail.
    if (Array.isArray(data.organizations)) {
      localStorage.setItem('available_organizations', JSON.stringify(data.organizations));
    }
    // Cache the active organization_id and default_location BEFORE setToken/setUser so
    // ExperienceProvider and landing screens immediately see the user's assigned site.
    if (data.default_organization?.id) {
      localStorage.setItem('current_organization_id', data.default_organization.id);
    } else {
      try {
        const ctxRes = await fetch(`${API_URL}/api/platform/context`, {
          headers: { Authorization: `Bearer ${data.token}` }
        });
        if (ctxRes.ok) {
          const ctx = await ctxRes.json();
          const orgId = ctx?.organizations?.[0]?.id;
          if (orgId) localStorage.setItem('current_organization_id', orgId);
        }
      } catch { /* ignore — components re-resolve on demand */ }
    }
    if (data.default_location?.id) {
      localStorage.setItem('current_location_id', data.default_location.id);
    }
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(apiErrorMessage(err.detail, 'Registration failed'));
    }
    const data = await res.json();
    localStorage.setItem('token', data.token);
    localStorage.setItem('auth_token', data.token);  // mobile shell SSO
    if (data.default_organization?.id) {
      localStorage.setItem('current_organization_id', data.default_organization.id);
    }
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    void signOutOnServer({ apiUrl: API_URL, token }); // asks the server to revoke this session; never blocks sign-out
    clearOperandiRetryState();
    clearWorkforceRetryState();
    void purgeReceivingDeviceState({ userId: user?.id, orgId: localStorage.getItem('current_organization_id') || '',
      locationId: localStorage.getItem('current_location_id') || '', department: readActiveDepartment() }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_organization_id');
    localStorage.removeItem('current_location_id');
    localStorage.removeItem('available_organizations');
    broadcastActiveDepartment(null);
    setToken(null);
    setUser(null);
  };

  const logoutAllDevices = async () => {
    await logoutAllDevicesAction({
      apiUrl: API_URL,
      token,
      logout,
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, login, completeLogin, register, logout, logoutAllDevices, loading }}>
      {children}
      <UpdateBanner />
      <IdleLock user={user} token={token} apiUrl={API_URL} onSignOut={logout} />
    </AuthContext.Provider>
  );
};

// Protected Route
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><Spinner /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
};

// /settings/security: turning two-factor off revokes every session, so leave for sign-in with a notice.
export const SecuritySettingsRoute = () => {
  const { token, logout, logoutAllDevices } = useAuth();
  const navigate = useNavigate();
  return (
    <SecuritySettings token={token} apiUrl={API_URL}
      onSignedOut={(notice) => { navigate('/login', { state: { notice } }); logout(); }}
      onLogoutAll={async () => { await logoutAllDevices(); navigate('/login'); }} />
  );
};

// Continuous-feedback button: signed-in app shell only (not public/guest/KDS screens).
const NO_FEEDBACK_PREFIXES = ['/guest/', '/pos/kds', '/login', '/trial', '/reset-password', '/forgot-password'];
const AuthedFeedback = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user || pathname === '/' || NO_FEEDBACK_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return <FeedbackButton />;
};

// Spinner Component
const Spinner = () => (
  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Workflow-first Navbar (Phase B of Client-Surfaces plan).
// 5 workspaces: Cut Cost · Make Money · Run Service · Stay Compliant · Settings.
// Existing module-config gating is preserved via `isEnabled(key)`.
// Every interactive element exposes a data-testid in kebab-case.
// ─────────────────────────────────────────────────────────────────────────────
const WORKSPACES = [
  {
    id: 'cut-cost',
    label: 'Cut Cost',
    icon: Sprout,
    items: [
      { label: 'Group view',          to: '/group',          icon: Building2,  gate: null,         testid: 'nav-group-view' },
      { label: 'Cost Intelligence',   to: '/cost-intel',     icon: TrendingUp, gate: null,         testid: 'nav-cut-cost-roi' },
      { label: 'Price impact',        to: '/pricing/impact', icon: TrendingUp, gate: 'calculator', testid: 'nav-cut-cost-price-impact' },
      { label: 'Review queue',        to: '/review',         icon: AlertTriangle, gate: null,      testid: 'nav-cut-cost-review', hideForDemoTenants: true },
      { label: 'Chef confirm',        to: '/chef-confirm',   icon: ChefHat, gate: null,            testid: 'nav-cut-cost-chef-confirm' },
      { label: 'Recipes',             to: '/recipes',        icon: Calculator, gate: null,         testid: 'nav-cut-cost-recipes' },
      { label: 'PPDS label',          to: '/recipes/ppds-label', icon: ClipboardCheck, gate: 'ppds_label', testid: 'nav-cut-cost-ppds-label' },
      { label: 'Vendors',             to: '/vendors',        icon: Building2,  gate: null,         testid: 'nav-cut-cost-vendors' },
      { label: 'Site Rollup',         to: '/roi',            icon: BarChart3,  gate: null,         testid: 'nav-cut-cost-site-rollup' },
      { label: 'Cost Calculator',     to: '/calculator',     icon: Calculator, gate: null,         testid: 'nav-cut-cost-calculator' },
      { label: 'Inventory Ledger',    to: '/inventory',      icon: Boxes,      gate: 'inventory',  testid: 'nav-cut-cost-inventory' },
      { label: 'CK operations',       to: '/ck-ops',         icon: ChefHat,    gate: 'ck_ops',     testid: 'nav-cut-cost-ck-ops' },
      { label: 'Delivery billing',    to: '/ck-ops/billing', icon: FileText,   gate: 'ck_ops',     testid: 'nav-cut-cost-ck-billing' },
      { label: 'Price lists',         to: '/ck-ops/price-lists', icon: ClipboardCheck, gate: 'ck_ops', testid: 'nav-cut-cost-ck-price-lists' },
      { label: 'Scan station',        to: '/scan',           icon: Boxes,      gate: 'scan_gates', testid: 'nav-cut-cost-scan-station' },
      { label: 'Receiving temperatures', to: '/receiving/settings', icon: ChefHat, gate: 'ck_ops', testid: 'nav-receiving-settings' },
      { label: 'Capture delivery note', to: '/receiving/capture', icon: Camera, gate: 'ck_ops', testid: 'nav-receiving-capture' },
      { label: 'Inbound deliveries', to: '/m/today', icon: Truck, gate: 'ck_ops', testid: 'nav-receiving-queue' },
      { label: 'My receipts', to: '/m/inbox', icon: ClipboardCheck, gate: 'ck_ops', testid: 'nav-receiving-inbox' },
      { label: 'Receiving review', to: '/receiving/reviews', icon: ShieldCheck, gate: 'ck_ops', testid: 'nav-receiving-reviews' },
      { label: 'Putaway', to: '/receiving/putaway', icon: Boxes, gate: 'ck_ops', testid: 'nav-receiving-putaway' },
      { label: 'Supplier claims', to: '/receiving/claims', icon: Mail, gate: 'ck_ops', testid: 'nav-receiving-claims' },
      { label: 'Offline sync', to: '/m/sync', icon: RefreshCw, gate: 'ck_ops', testid: 'nav-receiving-sync' },
      { label: 'Waste',               to: '/waste',          icon: Trash2,     gate: 'ck_ops',     testid: 'nav-cut-cost-waste' },
      { label: 'Decisions review',    to: '/decisions',      icon: BarChart3,  gate: 'decisions',  testid: 'nav-cut-cost-decisions' },
      { label: 'Marketplace',         to: '/marketplace',    icon: ShoppingCart, gate: 'marketplace', testid: 'nav-cut-cost-marketplace' },
      { label: 'CK marketplace ops',  to: '/marketplace-ops', icon: Boxes,     gate: 'marketplace', testid: 'nav-cut-cost-marketplace-ops' },
      { label: 'Procurement',         to: '/procurement',    icon: ShoppingCart, gate: null,       testid: 'nav-cut-cost-procurement' },
      { label: 'Supplier price lists', to: '/supplier-price-lists', icon: ClipboardCheck, gate: 'supplier_offers', testid: 'nav-cut-cost-supplier-price-lists' },
      { label: 'Demand Forecast',     to: '/forecast',       icon: TrendingDown, gate: null,       testid: 'nav-cut-cost-forecast' },
      { label: 'Ingredient Demand',   to: '/demand-forecast', icon: TrendingDown, gate: 'forecast', testid: 'nav-cut-cost-demand-forecast' },
      { label: 'Menu Costing',        to: '/menu-items',     icon: Utensils,   gate: 'directconnect', testid: 'nav-cut-cost-menu-items' },
    ],
  },
  {
    id: 'make-money',
    label: 'Make Money',
    icon: Banknote,
    items: [
      { label: 'POS Order Entry',     to: '/pos/order-entry', icon: ShoppingCart, gate: null,           testid: 'nav-make-money-pos' },
      { label: 'Kitchen Display',     to: '/pos/kds',         icon: ChefHat,      gate: null,           testid: 'nav-make-money-kds' },
      { label: 'Close of Day',        to: '/pos/close',       icon: Banknote,     gate: null,           testid: 'nav-make-money-close' },
      { label: 'Tables / Floor',      to: '/pos/tables',      icon: MapPin,       gate: null,           testid: 'nav-make-money-tables' },
      { label: 'Events',              to: '/events',          icon: PartyPopper,  gate: 'events',       testid: 'nav-make-money-events' },
      { label: 'CRM (Guests)',        to: '/crm',             icon: Users,        gate: null,           testid: 'nav-make-money-crm', hideForDemoTenants: true },
      { label: 'VoiceConcierge',      to: '/voice',           icon: Phone,        gate: null,           testid: 'nav-make-money-voice' },
      { label: 'Event Pro',           to: '/enhanced-events', icon: PartyPopper,  gate: 'enhanced-events', testid: 'nav-make-money-event-pro' },
      { label: 'Room Booking',        to: '/room-booking',    icon: Calendar,     gate: 'room-booking', testid: 'nav-make-money-rooms' },
      { label: 'Delivery',            to: '/delivery',        icon: Send,         gate: 'delivery',     testid: 'nav-make-money-delivery' },
      { label: 'DirectConnect',       to: '/directconnect',   icon: MessageSquare,gate: 'directconnect',testid: 'nav-make-money-directconnect' },
    ],
  },
  {
    id: 'run-service',
    label: 'Run Service',
    icon: Wrench,
    items: [
      { label: 'Onboarding',          to: '/onboarding',     icon: ChevronRight, gate: null, testid: 'nav-run-service-onboarding' },
      { label: 'First-run wizard',    to: '/first-run',      icon: ChevronRight, gate: null, testid: 'nav-run-service-first-run' },
      { label: 'Workforce',           to: '/workforce',      icon: Users,        gate: 'workforce', testid: 'nav-run-service-workforce' },
      { label: 'HR & Rota',           to: '/hrm',            icon: Users,        gate: null, testid: 'nav-run-service-hrm', hideForDemoTenants: true },
      { label: 'Menu Configuration',  to: '/menu-config',    icon: Settings,     gate: 'directconnect', testid: 'nav-run-service-menu-config' },
      { label: 'Integrations (n8n)',  to: '/n8n-config',     icon: Workflow,     gate: null, testid: 'nav-run-service-n8n' },
      { label: 'AI Demos',            to: '/demos',          icon: Bot,          gate: null, testid: 'nav-run-service-demos' },
      { label: 'Process discovery',   to: '/process-discovery', icon: Brain, gate: null, testid: 'nav-run-service-process-discovery', authed: true },
      // "Mobile App" nav item removed (demo-review-2026-09-09 P0): the
      // mobile app is broken (every screen throws user.getIdToken is not a
      // function) and no longer auto-launched for phone visitors — putting
      // a working nav link back in front of it would just make the broken
      // experience easier to find. Restore once mobile itself is fixed.
    ],
  },
  {
    id: 'stay-compliant',
    label: 'Stay Compliant',
    icon: ShieldCheck,
    items: [
      { label: 'Alerts (cockpit)',    to: '/alerts',         icon: AlertTriangle, gate: null, testid: 'nav-stay-compliant-alerts' },
      { label: 'Secure Documents',    to: '/documents',      icon: ShieldCheck,   gate: null, testid: 'nav-stay-compliant-documents' },
      { label: 'Safety & Health',     to: '/safety',         icon: ShieldCheck,   gate: 'safety', testid: 'nav-stay-compliant-safety' },
      { label: 'Contractor jobs',    to: '/contractor-jobs', icon: ClipboardCheck, gate: null, testid: 'nav-stay-compliant-contractors' },
      { label: 'Controls',            to: '/controls',       icon: ShieldCheck,   gate: 'controls', testid: 'nav-stay-compliant-controls' },
      { label: 'Visual safety',       to: '/visual-safety',  icon: ShieldCheck,   gate: 'vision_safety', testid: 'nav-stay-compliant-visual-safety' },
      { label: 'Case Studies',        to: '/case-studies',   icon: Award,         gate: null, testid: 'nav-stay-compliant-case-studies' },
    ],
  },
  {
    id: 'traceability',
    label: 'Traceability',
    icon: Boxes,
    items: [
      { label: 'Scan gates',   to: '/scan/gates', icon: ShieldCheck, gate: 'scan_gates', testid: 'nav-traceability-gates' },
      { label: 'Scan station', to: '/scan',       icon: Boxes,       gate: 'scan_gates', testid: 'nav-traceability-station' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    items: [
      { label: 'Dashboard',           to: '/dashboard',      icon: LayoutDashboard, gate: null, testid: 'nav-settings-dashboard', authed: true },
      { label: 'Data Room',           to: '/data-room',      icon: BarChart3,       gate: null, testid: 'nav-settings-dataroom', authed: true },
      { label: 'Messaging & Voice',   to: '/settings/integrations', icon: MessageSquare, gate: null, testid: 'nav-settings-integrations', authed: true },
      { label: 'Security',            to: '/settings/security',     icon: Shield,        gate: null, testid: 'nav-settings-security',     authed: true },
      { label: 'Setup',               to: '/settings/setup',        icon: ClipboardCheck, gate: null, testid: 'nav-settings-setup',       authed: true },
      { label: 'Compliance',          to: '/settings/compliance',   icon: ShieldCheck,    gate: null, testid: 'nav-settings-compliance',  authed: true },
      { label: 'Import data',         to: '/settings/imports',      icon: Download,       gate: null, testid: 'nav-settings-imports',     authed: true },
      { label: 'Destinations',        to: '/settings/destinations', icon: MapPin,        gate: 'ck_ops', testid: 'nav-settings-destinations', authed: true },
      { label: 'AI & automation',     to: '/settings/ai',           icon: Sparkles,      gate: null, testid: 'nav-settings-ai',           authed: true },
      { label: 'Persona onboarding', to: '/onboarding/persona', icon: Building2, gate: null, testid: 'nav-settings-persona', authed: true },
      { label: 'Team & Roles',        to: '/team',           icon: Users,           gate: null, testid: 'nav-settings-team',      authed: true },
      { label: 'Access & Permissions', to: '/admin/access', icon: Shield,          gate: null, testid: 'nav-settings-access',    authed: true },
      { label: 'Billing',             to: '/billing',        icon: CreditCard,      gate: null, testid: 'nav-settings-billing', hideForDemoTenants: true },
      { label: 'Xero Export',         to: '/finance/xero',   icon: Landmark,        gate: null, testid: 'nav-settings-xero', authed: true },
      { label: 'Superadmin',          to: '/admin/superadmin',icon: Shield,         gate: null, testid: 'nav-settings-superadmin', authed: true },
      { label: 'Implementations',     to: '/implementations',icon: Target,          gate: 'implementations', testid: 'nav-settings-implementations', authed: true },
      { label: 'Proposals',           to: '/proposals',      icon: FileText,        gate: 'proposals',       testid: 'nav-settings-proposals',       authed: true },
    ],
  },
];

// A menu item is drawn when the server's experience lists it (its module is on, the tenant's allow-list and persona
// menu include it) and its own capability check passes. Nothing is drawn before the experience arrives.
const navItemVisible = (it, nav, isEnabled, user) =>
  nav.has(it.testid) && (it.gate ? isEnabled(it.gate) : true) && (it.authed ? !!user : true);

// Every screen the menus can open, for the route guard (the workspaces plus the top-bar Invoices link).
const MENU_SCREENS = [{ to: '/invoices', testid: 'nav-invoices' }];

const WorkspaceMenu = ({ ws, isEnabled, user, openId, setOpenId, nav }) => {
  const open = openId === ws.id;
  const Icon = ws.icon;
  const label = useLabel();
  const close = () => setOpenId(null);
  const visible = ws.items.filter(it => navItemVisible(it, nav, isEnabled, user));
  if (visible.length === 0) return null;

  return (
    <div className="relative" data-testid={`workspace-${ws.id}`}>
      <button
        onClick={() => setOpenId(open ? null : ws.id)}
        onMouseEnter={() => setOpenId(ws.id)}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium motion-organic
          ${open ? 'bg-primary-50 text-primary-700' : 'text-ink-muted hover:text-primary-500 hover:bg-primary-50'}`}
        data-testid={`workspace-${ws.id}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon className="w-4 h-4" />
        <span>{label(`workspace:${ws.id}`, ws.label)}</span>
        <ChevronDown className={`w-3.5 h-3.5 motion-organic ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          onMouseLeave={close}
          className="absolute left-0 top-full mt-1 min-w-[240px] bg-white border border-surface-border rounded-lg shadow-ambient py-2 z-50"
          data-testid={`workspace-${ws.id}-menu`}
        >
          {visible.map((it, idx) => {
            const ItemIcon = it.icon;
            return (
              <Link
                key={idx}
                to={it.to}
                onClick={close}
                data-testid={it.testid}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-primary-50 hover:text-primary-700 motion-organic"
              >
                <ItemIcon className="w-4 h-4 text-ink-muted" />
                <span>{label(it.testid, it.label)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const { user, logout, logoutAllDevices, token } = useAuth();
  // The deployment's flavour: name, mark, menu words and switches (src/brand/BrandProvider.js).
  const { brand, features } = useBrand();
  const label = useLabel();
  // What to draw comes from the server's experience for the selected organisation (experience/experience.js).
  const exp = useExperience();
  // Each module's own /capabilities check (role and site rules) runs only once the experience says the module is on:
  // a module that is off costs no request and no 403, and nothing is probed while the answer is loading or unreadable.
  const ckEnabled = useCkNavEnabled(token, apiAllowed(exp, '/api/ck-ops/capabilities'));
  const decisionsEnabled = useDecisionsNavEnabled(token, apiAllowed(exp, '/api/decisions/capabilities'));
  const marketplaceEnabled = useMarketplaceNavEnabled(token, apiAllowed(exp, '/api/marketplace/capabilities'));
  const controlsEnabled = useControlsNavEnabled(token, apiAllowed(exp, '/api/controls/capabilities'));
  const workforceEnabled = useWorkforceNavEnabled(token, apiAllowed(exp, '/api/workforce/capabilities'));
  const forecastEnabled = useForecastNavEnabled(token, apiAllowed(exp, '/api/demand-forecast/capabilities'));
  const supplierOffersEnabled = useSupplierPriceListsNavEnabled(token, apiAllowed(exp, '/api/supplier-offers/capabilities'));
  const visionEnabled = useVisionNavEnabled(token, apiAllowed(exp, '/api/vision/capabilities'));
  // PPDS label (G6): its own module, so it is offered only when the tenant has `ppds_labels` AND can
  // reach the endpoint that backs the screen (which lives inside costing, hence the module's
  // needs: [costing]). A tenant with costing but no PPDS no longer sees a screen it cannot open.
  const ppdsEnabled = usePpdsNavEnabled(token, moduleAllowedClient('ppds_labels', exp.keys, exp.departmentGrants)
    && apiAllowed(exp, '/api/costing/recipes'));
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const [activeDepartment, setActiveDepartmentState] = useState(readActiveDepartment);
  const [orgBadge, setOrgBadge] = useState({ name: '', tier: '' });

  useEffect(() => {
    const onDepartmentChanged = (e) => setActiveDepartmentState(e.detail || '');
    const onStorage = (e) => { if (e.key === ACTIVE_DEPARTMENT_KEY) setActiveDepartmentState(e.newValue || ''); };
    window.addEventListener(DEPARTMENT_CHANGED_EVENT, onDepartmentChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(DEPARTMENT_CHANGED_EVENT, onDepartmentChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    // The badge names the selected organisation (the one the org switcher stored), not the first membership.
    const loadBadge = async () => {
      try {
        if (!token) { setOrgBadge({ name: '', tier: '' }); return; }
        const ctxRes = await fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } });
        const ctx = await ctxRes.json();
        const selected = localStorage.getItem('current_organization_id');
        const orgs = ctx?.organizations || [];
        const org = orgs.find((o) => o.id === selected) || orgs[0];
        if (org) setOrgBadge({ name: org.name || '', tier: org.tier || org.plan || '' });
      } catch (e) {
        setOrgBadge({ name: '', tier: '' });
      }
    };
    loadBadge();
  }, [token]);

  // Close dropdowns on outside click / route change.
  useEffect(() => { setOpenId(null); setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Unbuilt surfaces (no backend) are forced off regardless of per-org config.
  const isEnabled = (k) => k === 'decisions' ? decisionsEnabled : k === 'ck_ops' ? ckEnabled : k === 'marketplace' ? marketplaceEnabled : k === 'controls' ? controlsEnabled : k === 'workforce' ? workforceEnabled : k === 'forecast' ? forecastEnabled : k === 'ppds_label' ? ppdsEnabled : k === 'supplier_offers' ? supplierOffersEnabled : k === 'vision_safety' ? visionEnabled : !isUnbuiltSurface(k) && moduleAllowedClient(k, exp.keys, exp.departmentGrants, activeDepartment);

  return (
    <nav className="bg-surface border-b border-surface-border sticky top-0 z-40" data-testid="app-navbar">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8" ref={containerRef}>
        <div className="flex justify-between items-center h-16">
          {/* Brand */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
              {/* The deployment's flavour names and marks the product (backend/modules/flavours/). */}
              <BrandMark />
              <span className="font-display font-medium text-lg tracking-tight text-ink" data-testid="brand-name">
                {brand.product_name}
              </span>
            </Link>
          </div>

          {/* Desktop: Invoices, then the 5 workspaces */}
          <div className="hidden lg:flex items-center gap-1">
            {user && exp.nav.has('nav-invoices') && (
              <Link
                to="/invoices"
                data-testid="nav-invoices"
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium motion-organic ${location.pathname.startsWith('/invoices') ? 'bg-primary-50 text-primary-700' : 'text-ink-muted hover:text-primary-500 hover:bg-primary-50'}`}
              >
                <FileText className="w-4 h-4" />
                <span>{label('nav-invoices', 'Invoices')}</span>
              </Link>
            )}
            {WORKSPACES.map(ws => (
              <WorkspaceMenu
                key={ws.id}
                ws={ws}
                isEnabled={isEnabled}
                user={user}
                openId={openId}
                setOpenId={setOpenId}
                nav={exp.nav}
              />
            ))}
          </div>

          {/* Right: org switcher, location switcher, org badge, user */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Renders nothing for single-org users; the only people who see it
                are members of more than one org (invited team members). */}
            <div data-testid="header-organization-switcher">
              <OrganizationSwitcher />
            </div>
            <div data-testid="header-location-switcher">
              <LocationSwitcher />
            </div>
            {orgBadge.name && (
              <span
                className="hidden xl:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
                data-testid="header-org-badge"
                title={orgBadge.tier && features.plan_badge ? `${orgBadge.name} · ${orgBadge.tier}` : orgBadge.name}
              >
                <Building2 className="w-3.5 h-3.5" />
                {orgBadge.name}
                {orgBadge.tier && features.plan_badge && <span className="text-ink-muted" data-testid="header-plan-badge">· {orgBadge.tier}</span>}
              </span>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                {isDesktop && <NotificationBell />}
                <Link
                  to={exp.landing || '/dashboard'}
                  className="text-sm text-ink-muted hover:text-primary-500 px-2 py-1 rounded-md motion-organic"
                  data-testid="header-user-name"
                >
                  {user.name || user.email}
                </Link>
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  className="p-2 rounded-md text-ink-muted hover:text-danger hover:bg-surface-sunken motion-organic"
                  title="Log out"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { void logoutAllDevices().then(() => navigate('/')); }}
                  className="text-xs text-ink-muted hover:text-danger px-2 py-1 rounded-md motion-organic"
                  title="Log out of all devices"
                  data-testid="header-logout-all-btn"
                >
                  Log out of all devices
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-primary-500 text-surface px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 motion-organic"
                data-testid="header-get-started-btn"
              >
                Get Started
              </Link>
            )}
          </div>

          {/* Mobile burger, with Invoices and the bell one tap away */}
          <div className="lg:hidden flex items-center gap-1">
            {user && exp.nav.has('nav-invoices') && (
              <Link
                to="/invoices"
                data-testid="mobile-nav-invoices"
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-md text-sm font-medium text-ink hover:text-primary-500"
              >
                <FileText className="w-5 h-5" />
                <span className="hidden sm:inline">{label('nav-invoices', 'Invoices')}</span>
              </Link>
            )}
            {user && !isDesktop && <NotificationBell />}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-md text-ink-muted hover:text-primary-500"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-surface border-t border-surface-border" data-testid="mobile-nav-drawer">
          <div className="px-4 py-3 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {WORKSPACES.map(ws => {
              const visible = ws.items.filter(it => navItemVisible(it, exp.nav, isEnabled, user));
              if (visible.length === 0) return null;
              const Icon = ws.icon;
              return (
                <div key={ws.id} className="border border-surface-border rounded-md" data-testid={`mobile-workspace-${ws.id}`}>
                  <div className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-overline text-ink-muted bg-surface-sunken rounded-t-md">
                    <Icon className="w-3.5 h-3.5" />
                    {label(`workspace:${ws.id}`, ws.label)}
                  </div>
                  <div className="py-1">
                    {visible.map((it, idx) => (
                      <Link
                        key={idx}
                        to={it.to}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center min-h-[44px] px-4 py-2 text-sm text-ink hover:bg-primary-50 hover:text-primary-700"
                        data-testid={`mobile-${it.testid}`}
                      >
                        {label(it.testid, it.label)}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-surface-border">
              {user ? (
                <>
                <a
                  href="/mobile/dashboard"
                  className="block px-3 py-2 text-sm text-primary-700 font-medium"
                  data-testid="mobile-switch-to-mobile-view"
                >
                  Switch to mobile view
                </a>
                <button
                  onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-danger"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4 inline mr-2" /> Log out ({user.email})
                </button>
                <button
                  type="button"
                  onClick={() => { void logoutAllDevices().then(() => { navigate('/'); setMobileOpen(false); }); }}
                  className="w-full text-left px-3 py-2 text-sm text-ink-muted"
                  data-testid="mobile-logout-all-btn"
                >
                  Log out of all devices
                </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-primary-500"
                  data-testid="mobile-get-started-btn"
                >
                  Get Started
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      {user && !mobileOpen && features.mobile_offer && <MobileViewOffer pathname={location.pathname} />}
    </nav>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { brand } = useBrand();

  const stats = [
    { value: '<24h', label: 'Time to first plate costs', icon: Clock },
    { value: '3–20', label: 'Site groups (UK wedge)', icon: Target },
    { value: 'CK', label: 'Transfer prices you can audit', icon: PoundSterling },
    { value: '£49', label: 'From / site / month', icon: TrendingUp }
  ];

  const features = [
    { icon: Package, title: 'Invoice → cost model', desc: 'Forward supplier invoices; AI builds items and prices with a review queue for the fuzzy 10%.', savings: 'Hours, not consultants' },
    { icon: BarChart3, title: 'Live dish GP%', desc: 'Price moves flow to recipe and menu margins with an audit trail.', savings: 'Trustworthy plate costs' },
    { icon: Share2, title: 'Central kitchen transfers', desc: 'Cost / cost-plus / market rules per item — numbers finance can defend.', savings: 'Investor-ready' },
    { icon: Calendar, title: 'Multi-site roll-up', desc: 'Per-site COGS and holding-company view for UK groups.', savings: 'One cost spine' },
    { icon: MessageSquare, title: 'Chef-readable setup', desc: 'First-wow path: upload → review → cost a dish → see GP%.', savings: 'Start free trial' },
    { icon: Phone, title: 'Design-partner pilots', desc: 'Evidence pack + reconcile against your own dish costs.', savings: 'Proof before scale' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero — FoodOps cost wedge (working title; see brand REBRAND-READY). */}
      <section
        className="relative text-white overflow-hidden hero-pattern"
        style={{
          background:
            'linear-gradient(180deg, rgba(11,15,14,0.25) 0%, rgba(11,15,14,0.85) 55%, #0b0f0e 100%), radial-gradient(120% 80% at 70% 10%, rgba(210,179,106,0.28), transparent 55%), #0b0f0e',
        }}
      >
        <div className="relative max-w-6xl mx-auto px-4 py-24 md:py-28">
          <p className="font-display text-2xl md:text-3xl tracking-tight text-[#d2b36a] mb-6" data-testid="landing-brand">
            FoodOps
          </p>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl text-white" data-testid="landing-hero-title">
            Forward last night’s invoices. See plate costs tomorrow.
          </h1>
          <p className="text-lg md:text-xl mt-6 text-white/90 max-w-2xl">
            Trustworthy dish GP and central-kitchen transfer prices for UK multi-site groups — without a consultant army.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-10">
            <button
              onClick={() => navigate('/trial')}
              className="bg-[#d2b36a] text-[#0b0f0e] px-7 py-3.5 rounded-md font-semibold text-base hover:bg-[#e0c585] motion-organic flex items-center justify-center gap-2"
              data-testid="start-trial-btn"
            >
              Forward invoices
            </button>
            <button
              onClick={() => navigate('/first-run')}
              className="border border-white/60 text-white px-7 py-3.5 rounded-md font-semibold text-base hover:bg-white/10 motion-organic flex items-center justify-center gap-2"
              data-testid="see-how-btn"
            >
              See how it works
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-surface">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 rounded-md bg-primary-50 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-primary-500" />
                </div>
                <div className="font-display text-3xl md:text-4xl text-ink tabular-nums">{stat.value}</div>
                <div className="text-ink-muted text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-surface-muted">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-12">
            <div className="text-xs uppercase tracking-overline text-accent-500 mb-2">Modules</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink max-w-2xl">
              8 optimisation initiatives, one OS
            </h2>
            <p className="text-lg text-ink-muted max-w-2xl mt-3">
              A phased approach to transforming a restaurant from spreadsheets into a measured operation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 mb-3">{feature.desc}</p>
                <div className="inline-flex items-center text-primary-500 font-medium text-sm">
                  <TrendingUp className="w-4 h-4 mr-1" /> {feature.savings}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join restaurants saving £157K+ annually with AI automation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/calculator')}
              className="bg-primary-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-600 transition"
              data-testid="get-free-analysis-btn"
            >
              Get Free Analysis <ArrowRight className="w-5 h-5 inline ml-2" />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border border-gray-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-800 transition"
            >
              Book Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <BrandLockup dark />
            </div>
            <div className="text-gray-400 text-sm">
              © {new Date().getFullYear()} {brand.product_name}. All rights reserved.
            </div>
          </div>
          <ReleaseStamp />
        </div>
      </footer>
    </div>
  );
};

// Cost Calculator Page
const CalculatorPage = () => {
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [inputs, setInputs] = useState({
    pos_monthly_cost: 189,
    reservation_monthly_cost: 700,
    delivery_commission_rate: 28,
    monthly_delivery_revenue: 8000,
    phone_staff_hours_weekly: 4,
    hourly_wage: 15,
    inventory_manager_hours_weekly: 8,
    social_media_hours_weekly: 10,
    food_waste_percentage: 5,
    monthly_food_cost: 22000,
    staff_scheduling_hours_weekly: 4,
    payroll_monthly_cost: 500
  });

  const handleChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const calculateSavings = async () => {
    setLoading(true);
    try {
      const endpoint = user ? '/api/cost-analysis/save' : '/api/cost-analysis/calculate';

      // For now: require org context even for calculator (backend enforces it).
      // We'll pick the first org from platform context.
      const ctxRes = await fetch(`${API_URL}/api/platform/context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ctxJson = await ctxRes.json();
      const orgId = ctxJson?.organizations?.[0]?.id;
      const locId = ctxJson?.organizations?.[0]?.locations?.[0]?.id;

      const headers = { 'Content-Type': 'application/json' };
      if (user) headers.Authorization = `Bearer ${token}`;
      if (orgId) headers['X-Organization-Id'] = orgId;
      if (locId) headers['X-Location-Id'] = locId;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organization_id: orgId,
          location_id: locId,
          department: null,
          ...inputs
        })
      });
      const data = await res.json();
      setResults(user ? data.results : data);
      setStep(3);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#f97316', '#ea580c', '#c2410c', '#9a3412', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5'];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {['Your Costs', 'Operations', 'Results'].map((label, idx) => (
              <div key={idx} className={`flex items-center ${idx < step ? 'text-primary-500' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${idx + 1 <= step ? 'bg-primary-500 text-white' : 'bg-gray-200'}`}>
                  {idx + 1 < step ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <span className="ml-2 hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div className="h-2 bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }}></div>
          </div>
        </div>

        {/* Step 1: Current Costs */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Tell us about your current costs</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">POS Monthly Cost (£)</label>
                <input aria-label="POS Monthly Cost (£)"
                  type="number"
                  value={inputs.pos_monthly_cost}
                  onChange={(e) => handleChange('pos_monthly_cost', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 189"
                  data-testid="pos-cost-input"
                />
                <p className="text-xs text-gray-500 mt-1">Lightspeed: ~£189/mo, Toast: ~£55/mo</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reservation System Monthly (£)</label>
                <input aria-label="Reservation System Monthly (£)"
                  type="number"
                  value={inputs.reservation_monthly_cost}
                  onChange={(e) => handleChange('reservation_monthly_cost', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 700"
                  data-testid="reservation-cost-input"
                />
                <p className="text-xs text-gray-500 mt-1">SevenRooms: ~£700/mo, Resy: ~£200-400/mo</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Commission Rate (%)</label>
                <input aria-label="Delivery Commission Rate (%)"
                  type="number"
                  value={inputs.delivery_commission_rate}
                  onChange={(e) => handleChange('delivery_commission_rate', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 28"
                  data-testid="commission-rate-input"
                />
                <p className="text-xs text-gray-500 mt-1">Deliveroo/Uber Eats: 25-35%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Delivery Revenue (£)</label>
                <input aria-label="Monthly Delivery Revenue (£)"
                  type="number"
                  value={inputs.monthly_delivery_revenue}
                  onChange={(e) => handleChange('monthly_delivery_revenue', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 8000"
                  data-testid="delivery-revenue-input"
                />
              </div>
            </div>
            <button
              onClick={() => setStep(2)}
              className="mt-8 w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition"
              data-testid="next-step-btn"
            >
              Next: Operations <ChevronRight className="w-5 h-5 inline" />
            </button>
          </div>
        )}

        {/* Step 2: Operations */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Tell us about your operations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone/Reservation Staff Hours/Week</label>
                <input aria-label="Phone/Reservation Staff Hours/Week"
                  type="number"
                  value={inputs.phone_staff_hours_weekly}
                  onChange={(e) => handleChange('phone_staff_hours_weekly', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="phone-hours-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Average Hourly Wage (£)</label>
                <input aria-label="Average Hourly Wage (£)"
                  type="number"
                  value={inputs.hourly_wage}
                  onChange={(e) => handleChange('hourly_wage', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="hourly-wage-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Management Hours/Week</label>
                <input aria-label="Inventory Management Hours/Week"
                  type="number"
                  value={inputs.inventory_manager_hours_weekly}
                  onChange={(e) => handleChange('inventory_manager_hours_weekly', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="inventory-hours-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Hours/Week</label>
                <input aria-label="Social Media Hours/Week"
                  type="number"
                  value={inputs.social_media_hours_weekly}
                  onChange={(e) => handleChange('social_media_hours_weekly', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="social-hours-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Food Waste Percentage (%)</label>
                <input aria-label="Food Waste Percentage (%)"
                  type="number"
                  value={inputs.food_waste_percentage}
                  onChange={(e) => handleChange('food_waste_percentage', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="waste-percentage-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Food Cost (£)</label>
                <input aria-label="Monthly Food Cost (£)"
                  type="number"
                  value={inputs.monthly_food_cost}
                  onChange={(e) => handleChange('monthly_food_cost', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="food-cost-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Scheduling Hours/Week</label>
                <input aria-label="Scheduling Hours/Week"
                  type="number"
                  value={inputs.staff_scheduling_hours_weekly}
                  onChange={(e) => handleChange('staff_scheduling_hours_weekly', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="scheduling-hours-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Payroll Cost (£)</label>
                <input aria-label="Monthly Payroll Cost (£)"
                  type="number"
                  value={inputs.payroll_monthly_cost}
                  onChange={(e) => handleChange('payroll_monthly_cost', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="payroll-cost-input"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(1)}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                onClick={calculateSavings}
                disabled={loading}
                className="flex-1 bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
                data-testid="calculate-btn"
              >
                {loading ? <Spinner /> : <>Calculate Savings <Calculator className="w-5 h-5 ml-2" /></>}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && results && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Current Annual Costs</div>
                <div className="text-2xl font-bold text-gray-900">£{results.current_annual_costs.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Future Annual Costs</div>
                <div className="text-2xl font-bold text-green-600">£{results.future_annual_costs.toLocaleString()}</div>
              </div>
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 shadow-sm text-white">
                <div className="text-white/80 text-sm mb-1">Total Annual Savings</div>
                <div className="text-2xl font-bold">£{results.total_annual_savings.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-gray-500 text-sm mb-1">Year 1 ROI</div>
                <div className="text-2xl font-bold text-primary-500">{results.roi_percentage}%</div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings by Initiative</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={results.initiatives} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `£${v.toLocaleString()}`} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                    <Bar dataKey="savings" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Breakdown</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(results.breakdown).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: £${value.toLocaleString()}`}
                      labelLine={false}
                    >
                      {Object.entries(results.breakdown).map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Implementation Roadmap */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Implementation Roadmap</h3>
              <div className="space-y-4">
                {[1, 2, 3].map(phase => (
                  <div key={phase} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Phase {phase}: {phase === 1 ? 'Quick Wins (Weeks 1-4)' : phase === 2 ? 'Core Operations (Months 2-4)' : 'Technology Transformation (Months 5-6+)'}</h4>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {results.initiatives.filter(i => i.phase === phase).map((init, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="font-medium text-gray-900 text-sm">{init.name}</div>
                          <div className="text-primary-500 font-semibold">£{init.savings.toLocaleString()}/year</div>
                          <div className="text-xs text-gray-500">Payback: {init.payback_weeks} weeks</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Share Your Results</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <button
                  onClick={async () => {
                    const response = await fetch(`${API_URL}/api/reports/cost-analysis/generate-pdf`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(inputs)
                    });
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cost_analysis_report.pdf';
                    a.click();
                  }}
                  className="flex items-center justify-center gap-2 bg-primary-500 text-white px-4 py-3 rounded-lg hover:bg-primary-600 transition"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5" /> Download PDF Report
                </button>
                <button
                  onClick={() => {
                    const text = `Cost Analysis Results:\n• Annual Savings: £${results.total_annual_savings.toLocaleString()}\n• ROI: ${results.roi_percentage}%\n• Payback: ${results.payback_months} months`;
                    navigator.clipboard.writeText(text);
                    alert('Results copied to clipboard!');
                  }}
                  className="flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition"
                  data-testid="copy-results-btn"
                >
                  <Copy className="w-5 h-5" /> Copy Summary
                </button>
                <Link
                  to="/compare"
                  className="flex items-center justify-center gap-2 border border-primary-500 text-primary-500 px-4 py-3 rounded-lg hover:bg-primary-50 transition"
                  data-testid="compare-scenarios-btn"
                >
                  <GitCompare className="w-5 h-5" /> Compare Scenarios
                </Link>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gray-900 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Unlock These Savings?</h3>
              <p className="text-gray-300 mb-6">Book a free consultation to validate these numbers with your actual data.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition">
                  Book Consultation
                </Link>
                <button onClick={() => setStep(1)} className="border border-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition">
                  Recalculate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Demos Page
const DemosPage = () => {
  const [activeDemo, setActiveDemo] = useState('voice');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialContent, setSocialContent] = useState(null);

  const demos = [
    { id: 'voice', name: 'Voice AI', icon: Phone, desc: 'Try our AI phone assistant' },
    { id: 'whatsapp', name: 'WhatsApp Orders', icon: MessageSquare, desc: 'Experience direct ordering' },
    { id: 'social', name: 'Social Automation', icon: Share2, desc: 'Generate social content' }
  ];

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const endpoint = activeDemo === 'voice' ? '/api/demos/voice-ai' : '/api/demos/whatsapp';
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      console.error('Demo error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSocialContent = async (platform) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/demos/social-media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'post', platform })
      });
      const data = await res.json();
      setSocialContent({ platform, content: data.content });
    } catch (err) {
      console.error('Social demo error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">AI Demo Center</h1>
          <p className="text-gray-600">Experience our AI solutions in action</p>
        </div>

        {/* Demo Selector */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {demos.map(demo => (
            <button
              key={demo.id}
              onClick={() => { setActiveDemo(demo.id); setMessages([]); setSocialContent(null); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg transition whitespace-nowrap ${
                activeDemo === demo.id ? 'bg-primary-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`demo-${demo.id}-btn`}
            >
              <demo.icon className="w-5 h-5" />
              {demo.name}
            </button>
          ))}
        </div>

        {/* Demo Content */}
        {(activeDemo === 'voice' || activeDemo === 'whatsapp') && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-900 text-white p-4 flex items-center gap-3">
              {activeDemo === 'voice' ? <Phone className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
              <span className="font-medium">
                {activeDemo === 'voice' ? 'Curry on Naanstop AI Host' : 'WhatsApp Ordering Bot'}
              </span>
              <span className="ml-auto text-xs bg-green-500 px-2 py-1 rounded-full">Online</span>
            </div>
            
            <div className="h-96 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{activeDemo === 'voice' ? 'Try asking about reservations, menu, or hours!' : 'Type "order" or "menu" to start!'}</p>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs md:max-w-md px-4 py-3 rounded-2xl ${
                    msg.role === 'user' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-900'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder={activeDemo === 'voice' ? 'Ask about reservations...' : 'Type order, menu, or checkout...'}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="demo-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
                  data-testid="demo-send-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeDemo === 'social' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Social Media Content</h3>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {['instagram', 'facebook', 'twitter'].map(platform => (
                <button
                  key={platform}
                  onClick={() => generateSocialContent(platform)}
                  disabled={loading}
                  className="border border-gray-300 rounded-lg p-4 hover:border-primary-500 hover:bg-primary-50 transition text-center"
                  data-testid={`generate-${platform}-btn`}
                >
                  <div className="font-medium text-gray-900 capitalize">{platform}</div>
                  <div className="text-xs text-gray-500 mt-1">Generate post</div>
                </button>
              ))}
            </div>

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Spinner />
                <span className="ml-2 text-gray-600">Generating content...</span>
              </div>
            )}

            {socialContent && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900 capitalize">{socialContent.platform} Post</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(socialContent.content)}
                    className="text-primary-500 text-sm hover:underline"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{socialContent.content}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Case Studies Page
const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/case-studies`)
      .then(res => res.json())
      .then(data => { setCaseStudies(data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Customer Stories</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Results we can name, from operators who agreed to be named.
          </p>
        </div>

        {caseStudies.length === 0 && (
          <div
            className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm p-8 text-center"
            data-testid="case-studies-empty"
          >
            <p className="text-gray-900 font-semibold mb-2">No published stories yet.</p>
            <p className="text-gray-600 text-sm">
              We&apos;re an early product and we&apos;d rather show you nothing than
              show you someone else&apos;s numbers. Book a demo and we&apos;ll run the
              costing on your own menu — that result is yours, and it&apos;s the only
              one that should convince you.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {caseStudies.map((study) => (
            <div key={study.id} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`case-study-${study.id}`}>
              <div className="gradient-bg p-6">
                <span className="text-white/80 text-sm">{study.industry}</span>
                <h2 className="text-xl font-bold text-white mt-1">{study.title}</h2>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Challenge</h4>
                  <p className="text-gray-600 text-sm">{study.challenge}</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Solution</h4>
                  <p className="text-gray-600 text-sm">{study.solution}</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Results</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(study.results).map(([key, value]) => (
                      <div key={key} className="bg-primary-50 rounded-lg p-3">
                        <div className="text-primary-600 font-bold">{value}</div>
                        <div className="text-xs text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-600">
                  &quot;{study.quote}&quot;
                </blockquote>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '', interest: 'discovery' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ name: '', email: '', company: '', phone: '', message: '', interest: 'discovery' });
      }
    } catch (err) {
      console.error('Contact error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Let&apos;s Talk</h1>
          <p className="text-gray-600">Ready to transform your restaurant operations? Get in touch.</p>
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h2>
            <p className="text-gray-600">We&apos;ll be in touch within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input aria-label="Name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input aria-label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company/Restaurant *</label>
                <input aria-label="Company/Restaurant"
                  type="text"
                  required
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input aria-label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">I&apos;m interested in</label>
              <select aria-label="I&amp;apos;m interested in"
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="discovery">8-Week Discovery Engagement (£12,500)</option>
                <option value="demo">AI Demo & Consultation (Free)</option>
                <option value="implementation">Full Implementation Support</option>
                <option value="custom">Custom Solution</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea aria-label="Message"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Tell us about your restaurant and what challenges you're facing..."
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
              data-testid="contact-submit-btn"
            >
              {loading ? <Spinner /> : <>Send Message <Send className="w-5 h-5 ml-2" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Login/Register Page
export const AuthPage = () => {
  const { landing: brandLanding } = useBrand();
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ email: '', password: '', name: '', company: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);
  const { login, register, completeLogin } = useAuth();
  const navigate = useNavigate();
  const notice = useLocation().state?.notice;
  // A client's stack has sign-up off: people join by an invite link, so the page offers no "Sign up".
  const [selfSignup, setSelfSignup] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/auth/options`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => { if (o && o.self_signup === false) { setSelfSignup(false); setIsLogin(true); } })
      .catch(() => {});
  }, []);

  // Each persona starts on its own screen, as the server's experience says (menu files, backend/modules/menus/).
  const goToLanding = async (signedIn) => {
    let landing = null;
    try {
      const token = signedIn?.token || localStorage.getItem('token');
      if (token) {
        const exp = await fetchExperience(token, { locationId: signedIn?.default_location?.id || null });
        landing = landingFor(exp, GUARDED_MENU_SCREENS, POST_LOGIN_ROUTE);
      }
    } catch (e) {
      landing = null;
    }
    navigate(landing || POST_LOGIN_ROUTE);
  };

  if (mfaStep) {
    const backToSignIn = (message = '') => { setMfaStep(null); setError(message); };
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8">
          {mfaStep.kind === 'change' ? (
            <SetYourPassword changeToken={mfaStep.token} apiUrl={API_URL}
              onSignedIn={async (data) => {
                const step = classifyLoginResponse(data);
                if (step.kind === 'mfa') { setMfaStep({ kind: 'code', token: step.mfaToken }); return; }
                if (step.kind === 'enrol') { setMfaStep({ kind: 'enrol', token: step.mfaToken }); return; }
                goToLanding(await completeLogin(data));
              }}
              onBack={backToSignIn} />
          ) : mfaStep.kind === 'code' ? (
            <MfaCodeStep mfaToken={mfaStep.token} apiUrl={API_URL}
              onVerified={async (data) => goToLanding(await completeLogin(data))}
              onBack={() => backToSignIn()} onExpired={backToSignIn} />
          ) : (
            /* Forced enrolment: the enrolment token is the auth header, and the
               session only arrives from confirm, so completeLogin runs on Done.
               The token is component state (mfaStep) — never localStorage. */
            <div className="space-y-5" data-testid="web-forced-enrolment">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Set up two-factor sign-in to continue</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Your organisation requires it for your role.
                </p>
              </div>
              <MfaEnrol authHeader={mfaStep.token} apiUrl={API_URL}
                onDone={async (session) => {
                  if (!session) { backToSignIn(); return; }
                  goToLanding(await completeLogin(session));
                }} />
              <button type="button" onClick={() => backToSignIn()}
                className="min-h-[44px] px-2 text-sm text-blue-600 hover:underline"
                data-testid="web-enrol-back-btn">Back to sign in</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let signedIn;
      if (isLogin) {
        signedIn = await login(form.email, form.password);
        if (signedIn?.passwordChangeRequired) {
          setMfaStep({ kind: 'change', token: signedIn.changeToken });
          return;
        }
        if (signedIn?.mfaRequired || signedIn?.enrolmentRequired) {
          setMfaStep({ kind: signedIn.mfaRequired ? 'code' : 'enrol', token: signedIn.mfaToken });
          return;
        }
      } else {
        signedIn = await register({ ...form, role: 'restaurant_owner' });
      }
      goToLanding(signedIn);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6">
            <BrandLockup />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{isLogin ? brandLanding.signin_title : 'Create your account'}</h1>
          {isLogin && brandLanding.signin_subtitle && <p className="mt-1 text-sm text-ink-muted" data-testid="signin-subtitle">{brandLanding.signin_subtitle}</p>}
          <PoweredBy className="mt-2" />
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-8 space-y-6" data-testid="login-form">

        {/* (duplicated tag removed by platform update) */}
          {notice && !error && (
            <div role="status" className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm" data-testid="auth-notice">{notice}</div>
          )}
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          {!isLogin && (
            <>
              <div>
                <label htmlFor="auth-name-input" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input aria-label="Name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  id="auth-name-input"
                  data-testid="auth-name-input"
                />
              </div>
              <div>
                <label htmlFor="auth-company-input" className="block text-sm font-medium text-gray-700 mb-2">Company/Restaurant</label>
                <input aria-label="Company/Restaurant"
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  id="auth-company-input"
                  data-testid="auth-company-input"
                />
              </div>
            </>
          )}
          <div>
            {/* Sign-in takes an email or a username (backend/identity.py): some people have no email. */}
            <label htmlFor="auth-email-input" className="block text-sm font-medium text-gray-700 mb-2">{isLogin ? 'Email or username' : 'Email'}</label>
            <input aria-label={isLogin ? 'Email or username' : 'Email'}
              type={isLogin ? 'text' : 'email'}
              autoComplete={isLogin ? 'username' : 'email'}
              autoCapitalize="none"
              spellCheck={false}
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              id="auth-email-input"
                  data-testid="auth-email-input"
            />
          </div>
          <div>
            <label htmlFor="auth-password-input" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input aria-label="Password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              id="auth-password-input"
                  data-testid="auth-password-input"
            />
          </div>
          {isLogin && (
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-sm text-primary-500 hover:underline" data-testid="auth-forgot-password-link">
                Forgot password?
              </Link>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
            data-testid="auth-submit-btn"
          >
            {loading ? <Spinner /> : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
          <div className="text-center">
            {selfSignup ? (
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary-500 hover:underline text-sm"
              >
                {isLogin ? "Don\u2019t have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            ) : (
              <p className="text-sm text-gray-500" data-testid="auth-invite-only">
                New here? Ask your organisation&rsquo;s owner to add you; you will get a link to join.
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Forgot Password Page — requests a reset link. Always shows the same
// success message regardless of whether the email is registered (the
// backend enforces no-enumeration too; the UI shouldn't undermine that by
// e.g. showing a different message on a 404).
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err.detail, 'Could not send reset link'));
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6">
            <BrandLockup />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Reset your password</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-6" data-testid="forgot-password-form">
          {sent ? (
            <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm" data-testid="forgot-password-sent">
              If that email is registered, a reset link has been sent. Check your inbox — the link expires in 30 minutes.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}
              <div>
                <label htmlFor="forgot-password-email-input" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input aria-label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  id="forgot-password-email-input"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? <Spinner /> : 'Send reset link'}
              </button>
            </form>
          )}
          <div className="text-center">
            <Link to="/login" className="text-primary-500 hover:underline text-sm">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page — the link target from the email (`?token=...`).
// The invite link's page (auth/AcceptInvite.js): it signs the new person in with the normal sign-in.
const AcceptInviteRoute = () => {
  const { login } = useAuth();
  return <AcceptInvitePage signIn={login} />;
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err.detail, 'Reset link is invalid or has expired'));
      }
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6">
            <BrandLockup />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Choose a new password</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-6" data-testid="reset-password-form">
          {!token && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
              This link is missing its reset token. Request a new one from the sign-in page.
            </div>
          )}
          {done ? (
            <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm" data-testid="reset-password-done">
              Password updated. Redirecting you to sign in&hellip;
            </div>
          ) : (
            token && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
                )}
                <div>
                  <label htmlFor="reset-password-new-input" className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                  <input aria-label="New password"
                    type="password"
                    required
                    minLength={10}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    id="reset-password-new-input"
                    data-testid="reset-password-new-input"
                  />
                </div>
                <div>
                  <label htmlFor="reset-password-confirm-input" className="block text-sm font-medium text-gray-700 mb-2">Confirm new password</label>
                  <input aria-label="Confirm new password"
                    type="password"
                    required
                    minLength={10}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    id="reset-password-confirm-input"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? <Spinner /> : 'Update password'}
                </button>
              </form>
            )
          )}
          <div className="text-center">
            <Link to="/login" className="text-primary-500 hover:underline text-sm">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Platform context
  const [platformCtx, setPlatformCtx] = useState(null);
  const [activeOrgId, setActiveOrgId] = useState('');
  const [activeLocationId, setActiveLocationId] = useState('');
  const [activeDepartment, setActiveDepartment] = useState(readActiveDepartment);
  const [departments, setDepartments] = useState([]);
  const [customDepartmentInput, setCustomDepartmentInput] = useState('');
  const [moduleConfig, setModuleConfig] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

 

  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [newRestaurant, setNewRestaurant] = useState({ name: '', locations: 1, cuisine_type: 'Indian', annual_revenue: 0 });

  useEffect(() => {
    initAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initAndFetch = async () => {
    try {
      setLoading(true);
      const baseHeaders = { Authorization: `Bearer ${token}` };

      // 1) Fetch platform context (orgs + locations)
      const ctxRes = await fetch(`${API_URL}/api/platform/context`, { headers: baseHeaders });
      const ctxJson = await ctxRes.json();
      setPlatformCtx(ctxJson);

      const defaultOrgId = ctxJson?.organizations?.[0]?.id || '';
      const defaultLocId = ctxJson?.organizations?.[0]?.locations?.[0]?.id || '';

      const orgId = activeOrgId || defaultOrgId;
      const locId = activeLocationId || defaultLocId;

      setActiveOrgId(orgId);
      setActiveLocationId(locId);

      // 2) Fetch module config for org
      const cfgRes = await fetch(`${API_URL}/api/platform/module-config?organization_id=${encodeURIComponent(orgId)}`, {
        headers: baseHeaders
      });
      if (cfgRes.ok) setModuleConfig(await cfgRes.json());

      // 3) Fetch departments (fixed + custom)
      const deptRes = await fetch(`${API_URL}/api/platform/departments?organization_id=${encodeURIComponent(orgId)}`, {
        headers: baseHeaders
      });
      if (deptRes.ok) {
        const deptJson = await deptRes.json();
        setDepartments(deptJson.merged || deptJson.fixed || []);
      }

      // 4) Fetch onboarding status
      const onbRes = await fetch(`${API_URL}/api/platform/onboarding/status?organization_id=${encodeURIComponent(orgId)}`, {
        headers: baseHeaders
      });
      if (onbRes.ok) setOnboardingStatus(await onbRes.json());

      await fetchData(orgId, locId, activeDepartment);
    } catch (err) {
      console.error('Dashboard init error:', err);
      setLoading(false);
    }
  };

  const fetchData = async (orgId, locId, dept) => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': orgId,
        ...(locId ? { 'X-Location-Id': locId } : {}),
        ...(dept ? { 'X-Department': dept } : {})
      };

      const [analyticsRes, restaurantsRes, analysesRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/dashboard`, { headers }),
        fetch(`${API_URL}/api/restaurants`, { headers }),
        fetch(`${API_URL}/api/cost-analysis/history`, { headers })
      ]);

      setAnalytics(await analyticsRes.json());
      setRestaurants(await restaurantsRes.json());
      setAnalyses(await analysesRes.json());
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addRestaurant = async () => {
    try {
      const res = await fetch(`${API_URL}/api/restaurants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Organization-Id': activeOrgId,
          ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
          ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
        },
        body: JSON.stringify({
          organization_id: activeOrgId,
          ...newRestaurant
        })
      });
      if (res.ok) {
        setShowAddRestaurant(false);
        setNewRestaurant({ name: '', locations: 1, cuisine_type: 'Indian', annual_revenue: 0 });
        fetchData(activeOrgId, activeLocationId, activeDepartment);
      }
    } catch (err) {
      console.error('Error adding restaurant:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }


  const enabledModules = moduleConfig?.modules_enabled || {};
  const isModuleEnabled = (key) => enabledModules[key] !== false;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: LayoutDashboard, enabled: true },
    { id: 'restaurants', name: 'Restaurants', icon: Building2, enabled: true },
    { id: 'inventory', name: 'Inventory', icon: Package, enabled: isModuleEnabled('inventory') },
    { id: 'scheduling', name: 'Staff Scheduling', icon: Calendar, enabled: isModuleEnabled('scheduling') },
    { id: 'analytics', name: 'Analytics', icon: BarChart3, enabled: true },
    { id: 'platform', name: 'Platform', icon: Settings, enabled: true }
  ].filter(t => t.enabled);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.name}</h1>
            {/* Literal character, not \u2019: this is a JSX text node, where a
                backslash escape is not interpreted and rendered as raw text on
                the first screen after login. */}
            <p className="text-gray-600">Here’s your restaurant operations overview</p>
          </div>
          <button
            onClick={() => setShowAddRestaurant(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="add-restaurant-btn"
          >
            <Plus className="w-5 h-5" /> Add Restaurant
          </button>
        </div>

        {onboardingStatus && (!onboardingStatus.is_complete || (onboardingStatus.first_wow && !onboardingStatus.first_wow.ready)) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="onboarding-banner">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-amber-900" data-testid="onboarding-banner-title">
                  {(onboardingStatus.first_wow && onboardingStatus.first_wow.headline)
                    || `Finish setup for ${onboardingStatus.organization_name || 'your organization'}`}
                </div>
                {onboardingStatus.first_wow && (
                  <div className="text-xs text-amber-700 mt-1" data-testid="onboarding-banner-first-wow">
                    First wow: {onboardingStatus.first_wow.score}
                    {onboardingStatus.first_wow.ready ? ' — ready' : ''}
                  </div>
                )}
                <div className="text-sm text-amber-800 mt-1" data-testid="onboarding-banner-steps">
                  Next steps: {(onboardingStatus.next_steps || []).map((step) =>
                    (onboardingStatus.step_labels && onboardingStatus.step_labels[step]) || step
                  ).join(', ') || 'Open Cost Intelligence to keep going'}
                </div>
                {(onboardingStatus.recommended_steps || []).includes('add_suppliers') && (
                  <button type="button" onClick={() => navigate('/vendors?onboarding=suppliers')}
                    className="text-sm text-amber-900 underline mt-1" data-testid="onboarding-banner-suppliers">
                    {(onboardingStatus.step_labels && onboardingStatus.step_labels.add_suppliers) || 'Add and approve your suppliers'}
                  </button>
                )}
              </div>
              <button
                onClick={() => navigate(onboardingStatus.activation_path || (onboardingStatus.first_wow?.ready ? '/roi' : '/first-run'))}
                className="bg-amber-900 text-white px-3 py-2 rounded-lg"
                data-testid="onboarding-banner-cta"
              >
                {onboardingStatus.cta_label || (onboardingStatus.first_wow?.ready ? 'See GP%' : 'Start first wow')}
              </button>
            </div>
          </div>
        )}

        {/* Platform Context Selector (moved above tabs) */}
 
        <div className="bg-white rounded-xl p-4 border border-gray-100 mb-6" data-testid="platform-context-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select aria-label="Organization"
                value={activeOrgId}
                onChange={async (e) => {
                  const nextOrgId = e.target.value;
                  setActiveOrgId(nextOrgId);
                  const nextOrg = platformCtx?.organizations?.find(o => o.id === nextOrgId);
                  const nextLocId = nextOrg?.locations?.[0]?.id || '';
                  setActiveLocationId(nextLocId);
                  await fetchData(nextOrgId, nextLocId, activeDepartment);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="org-selector"
              >
                {(platformCtx?.organizations || []).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select aria-label="Location"
                value={activeLocationId}
                onChange={async (e) => {
                  const nextLocId = e.target.value;
                  setActiveLocationId(nextLocId);
                  await fetchData(activeOrgId, nextLocId, activeDepartment);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="location-selector"
              >
                {((platformCtx?.organizations || []).find(o => o.id === activeOrgId)?.locations || []).map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select aria-label="Department"
                value={activeDepartment}
                onChange={async (e) => {
                  const nextDept = e.target.value;
                  setActiveDepartment(nextDept);
                  broadcastActiveDepartment(nextDept);
                  await fetchData(activeOrgId, activeLocationId, nextDept);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="department-selector"
              >
                <option value="">All</option>
                {(departments || []).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3" data-testid="platform-context-hint">
            This selection controls which organization/location/department data you are viewing and editing.
          </p>
        </div>


            <div className="md:col-span-3" data-testid="custom-department-panel">
              <label className="block text-sm font-medium text-gray-700 mb-1">Add Custom Department</label>
              <div className="flex gap-2">
                <input aria-label="Add Custom Department"
                  value={customDepartmentInput}
                  onChange={(e) => setCustomDepartmentInput(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="e.g. banquets"
                  data-testid="custom-department-input"
                />
                <button
                  onClick={async () => {
                    if (!customDepartmentInput.trim()) return;
                    const next = Array.from(new Set([...(departments || []), customDepartmentInput.trim().toLowerCase()]));
                    const res = await fetch(`${API_URL}/api/platform/departments`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                        'X-Organization-Id': activeOrgId
                      },
                      body: JSON.stringify({ custom_departments: next.filter(d => !['kitchen','bar','foh','hr','finance','ops','admin'].includes(d)) })
                    });
                    if (res.ok) {
                      const deptRes = await fetch(`${API_URL}/api/platform/departments?organization_id=${encodeURIComponent(activeOrgId)}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (deptRes.ok) {
                        const deptJson = await deptRes.json();
                        setDepartments(deptJson.merged || deptJson.fixed || []);
                      }
                      setCustomDepartmentInput('');
                    }
                  }}
                  className="bg-gray-900 text-white px-4 py-2 rounded-lg"
                  data-testid="custom-department-add-button"
                >
                  Add
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Custom departments are organization-level configuration and will appear in the Department selector.
              </p>
            </div>



        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition whitespace-nowrap ${
                activeTab === tab.id ? 'bg-primary-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Add Restaurant Modal */}
        {showAddRestaurant && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-semibold mb-4">Add Restaurant</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name</label>
                  <input aria-label="Restaurant Name"
                    type="text"
                    value={newRestaurant.name}


                    onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Curry on Naanstop"
                    data-testid="restaurant-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Locations</label>
                  <input aria-label="Number of Locations"
                    type="number"
                    value={newRestaurant.locations}
                    onChange={(e) => setNewRestaurant({ ...newRestaurant, locations: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cuisine Type</label>
                  <select aria-label="Cuisine Type"
                    value={newRestaurant.cuisine_type}
                    onChange={(e) => setNewRestaurant({ ...newRestaurant, cuisine_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Indian">Indian</option>
                    <option value="Italian">Italian</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Mexican">Mexican</option>
                    <option value="Fast Casual">Fast Casual</option>
                    <option value="Fine Dining">Fine Dining</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue (£)</label>
                  <input aria-label="Annual Revenue (£)"
                    type="number"
                    value={newRestaurant.annual_revenue}
                    onChange={(e) => setNewRestaurant({ ...newRestaurant, annual_revenue: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddRestaurant(false)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={addRestaurant}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition"
                  data-testid="save-restaurant-btn"
                >
                  Add Restaurant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats */}
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Restaurants</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.total_restaurants}</p>
                    </div>
                    <Building2 className="w-8 h-8 text-primary-500" />
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Analyses</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.total_analyses}</p>
                    </div>
                    <Calculator className="w-8 h-8 text-primary-500" />
                  </div>
                </div>
                {isModuleEnabled('proposals') && (
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="overview-proposals-stat">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Proposals</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics.total_proposals}</p>
                      </div>
                      <FileText className="w-8 h-8 text-primary-500" />
                    </div>

 

                  </div>
                )}
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 shadow-sm text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm">Avg Savings</p>
                      <p className="text-2xl font-bold">£{(analytics.average_savings_identified || 0).toLocaleString()}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-white/80" />
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/calculator" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">New Analysis</h3>
                  <p className="text-sm text-gray-500">Calculate savings for a restaurant</p>
                </div>
              </Link>
              <Link to="/demos" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Demos</h3>
                  <p className="text-sm text-gray-500">Try voice AI and WhatsApp bots</p>
                </div>
              </Link>
              <Link to="/contact" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Get Support</h3>
                  <p className="text-sm text-gray-500">Book a consultation call</p>
                </div>
              </Link>
            </div>

            {/* Recent Analyses */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Analyses</h2>
              {analyses.length === 0 ? (
                <div className="text-center py-8">
                  <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No analyses yet. Run your first calculation!</p>
                  <Link to="/calculator" className="inline-block mt-4 text-primary-500 hover:underline">
                    Calculate Savings <ArrowRight className="w-4 h-4 inline" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {analyses.slice(0, 5).map((analysis) => (
                    <div key={analysis.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Analysis #{analysis.id.slice(0, 8)}</p>
                        <p className="text-sm text-gray-500">{new Date(analysis.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary-500">£{analysis.results.total_annual_savings.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">annual savings</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Restaurants Tab */}
        {activeTab === 'restaurants' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Restaurants</h2>
            {restaurants.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No restaurants added yet</p>
                <button
                  onClick={() => setShowAddRestaurant(true)}
                  className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition"
                >
                  Add Your First Restaurant
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {restaurants.map((restaurant) => (
                  <div key={restaurant.id} className="border rounded-xl p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        <Utensils className="w-5 h-5 text-primary-500" />
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Active</span>
                    </div>
                    <h3 className="font-semibold text-gray-900">{restaurant.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{restaurant.cuisine_type}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" /> {restaurant.locations} location{restaurant.locations > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Link
                        to="/calculator"
                        className="flex-1 text-center text-sm bg-primary-50 text-primary-600 py-2 rounded-lg hover:bg-primary-100 transition"
                      >
                        Analyze
                      </Link>
                      <button className="px-3 py-2 text-gray-400 hover:text-gray-600 transition">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <InventoryManager
            token={token}
            restaurants={restaurants}
            platformHeaders={{
              'X-Organization-Id': activeOrgId,
              ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
              ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
            }}
            activeOrgId={activeOrgId}
            activeLocationId={activeLocationId}
            activeDepartment={activeDepartment}
          />
        )}

        {/* Scheduling Tab */}
        {activeTab === 'scheduling' && (
          <SchedulingManager
            token={token}
            restaurants={restaurants}
            platformHeaders={{
              'X-Organization-Id': activeOrgId,
              ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
              ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
            }}
            activeOrgId={activeOrgId}
            activeLocationId={activeLocationId}
            activeDepartment={activeDepartment}
          />
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard analyses={analyses} />
        )}

        {/* Platform Tab */}
        {activeTab === 'platform' && (
          <PlatformAdmin
            token={token}
            activeOrgId={activeOrgId}
            activeLocationId={activeLocationId}
            activeDepartment={activeDepartment}
            platformHeaders={{
              'X-Organization-Id': activeOrgId,
              ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
              ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
            }}
          />
        )}
      </div>
    </div>
  );
};

// Platform Admin (Organizations/Locations/Devices/Memberships/Modules)
const PlatformAdmin = ({ token, activeOrgId, activeLocationId, activeDepartment, platformHeaders }) => {
  // API keys are the developer_access add-on. Asking for them when it is not bought is a refused call, so the
  // list is fetched, and the card drawn, only when the server's experience says the prefix is open.
  const exp = useExperience();
  const keysAllowed = apiAllowed(exp, '/api/auth/api-keys');
  const [memberships, setMemberships] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('Mobile Device Key');
  const [createdKey, setCreatedKey] = useState(null);
  const [deviceIdForKey, setDeviceIdForKey] = useState('');

  const [devices, setDevices] = useState([]);
  const [newDevice, setNewDevice] = useState({ name: '', device_type: 'tablet', identifier: '', department: 'kitchen' });
  const [creatingDevice, setCreatingDevice] = useState(false);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDept, setInviteDept] = useState('kitchen');
  const [inviteRole, setInviteRole] = useState('staff');
  const [saving, setSaving] = useState(false);
  const [platformActionError, setPlatformActionError] = useState(null);

  const fetchAll = async () => {
    try {
      const [mRes, dRes, cRes, kRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/memberships?organization_id=${encodeURIComponent(activeOrgId)}`, {
          headers: { Authorization: `Bearer ${token}`, ...(platformHeaders || {}) }
        }),
        fetch(`${API_URL}/api/platform/devices?organization_id=${encodeURIComponent(activeOrgId)}&location_id=${encodeURIComponent(activeLocationId || '')}`, {
          headers: { Authorization: `Bearer ${token}`, ...(platformHeaders || {}) }
        }),
        fetch(`${API_URL}/api/platform/module-config?organization_id=${encodeURIComponent(activeOrgId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        keysAllowed
          ? fetch(`${API_URL}/api/auth/api-keys`, { headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve({ ok: false })
      ]);

      if (mRes.ok) setMemberships(await mRes.json());
      if (dRes.ok) setDevices(await dRes.json());
      if (cRes.ok) setModuleConfig(await cRes.json());
      if (kRes.ok) setApiKeys(await kRes.json());
    } catch (e) {
      console.error('Platform fetch error', e);
    }
  };

  useEffect(() => {
    if (activeOrgId) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId, activeLocationId, keysAllowed]);

  const toggleModule = (key) => {
    const next = {
      ...(moduleConfig || { organization_id: activeOrgId, location_id: null, modules_enabled: {} }),
      organization_id: activeOrgId,
      location_id: null,
      modules_enabled: {
        ...(moduleConfig?.modules_enabled || {}),
        [key]: !(moduleConfig?.modules_enabled || {})[key]
      }
    };
    setModuleConfig(next);
  };

  const saveModuleConfig = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/platform/module-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(moduleConfig)
      });
      if (res.ok) {
        setModuleConfig(await res.json());
        setPlatformActionError(null);
      } else {
        const err = await res.json().catch(() => ({}));
        setPlatformActionError(apiErrorMessage(err.detail, `Could not save modules (${res.status})`));
      }
    } catch (e) {
      console.error('Save module config error', e);
      setPlatformActionError('Network error — could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async () => {
    try {
      if (!inviteEmail) return;
      const res = await fetch(`${API_URL}/api/platform/memberships/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify({
          organization_id: activeOrgId,
          email: inviteEmail,
          roles: [inviteRole],
          location_ids: activeLocationId ? [activeLocationId] : [],
          departments: inviteDept ? [inviteDept] : []
        })
      });
      if (res.ok) {
        setInviteEmail('');
        setPlatformActionError(null);
        await fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setPlatformActionError(apiErrorMessage(err.detail, `Could not send invite (${res.status})`));
      }
    } catch (e) {
      console.error('Invite error', e);
      setPlatformActionError('Network error — could not reach the server.');
    }
  };

  const updateMembership = async (membershipId, patch) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/memberships/${membershipId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify(patch)
      });
      if (res.ok) {
        setPlatformActionError(null);
        await fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setPlatformActionError(apiErrorMessage(err.detail, `Could not update membership (${res.status})`));
      }
    } catch (e) {
      console.error('Update membership error', e);
      setPlatformActionError('Network error — could not reach the server.');
    }
  };

  const modules = [
    { key: 'events', label: 'Events' },
    { key: 'inventory', label: 'Inventory' },
    { key: 'scheduling', label: 'Scheduling' },
    { key: 'proposals', label: 'Proposals' },
    { key: 'implementations', label: 'Implementations' },
    { key: 'calculator', label: 'Calculator' }
  ];


  const enabled = moduleConfig?.modules_enabled || {};

  const createApiKey = async () => {
    try {
      setCreatingKey(true);
      setCreatedKey(null);

      const params = new URLSearchParams();
      params.set('name', newKeyName || 'Mobile Device Key');
      params.set('organization_id', activeOrgId);
      if (deviceIdForKey) params.set('device_id', deviceIdForKey);
      if (activeLocationId) params.set('location_id', activeLocationId);

      const res = await fetch(`${API_URL}/api/auth/api-keys?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedKey(data);
        setPlatformActionError(null);
        await fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setPlatformActionError(apiErrorMessage(err.detail, `Could not create API key (${res.status})`));
      }
    } catch (e) {
      console.error('Create API key error', e);
      setPlatformActionError('Network error — could not reach the server.');
    } finally {
      setCreatingKey(false);
    }
  };

  const createDevice = async () => {
    try {
      if (!newDevice.identifier) return;
      setCreatingDevice(true);
      const res = await fetch(`${API_URL}/api/platform/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify({
          organization_id: activeOrgId,
          location_id: activeLocationId || null,
          department: newDevice.department || null,
          device_type: newDevice.device_type,
          identifier: newDevice.identifier,
          name: newDevice.name || null,
          capabilities: {}
        })
      });
      if (res.ok) {
        setNewDevice({ name: '', device_type: 'tablet', identifier: '', department: 'kitchen' });
        setPlatformActionError(null);
        await fetchAll();
        // extra safety for eventual consistency / UI refresh
        setTimeout(() => { fetchAll(); }, 500);
      } else {
        const err = await res.json().catch(() => ({}));
        setPlatformActionError(apiErrorMessage(err.detail, `Could not add device (${res.status})`));
      }
    } catch (e) {
      console.error('Create device error', e);
      setPlatformActionError('Network error — could not reach the server.');
    } finally {
      setCreatingDevice(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="platform-admin-panel">
      <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="platform-admin-module-gating-hint">
        <p className="text-sm text-gray-600">
          Module gating is active: disabled modules will be hidden/disabled in the main dashboard and navigation.
        </p>
      </div>

      {platformActionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="platform-admin-action-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{platformActionError}</span>
          </div>
          <button
            type="button"
            onClick={() => setPlatformActionError(null)}
            className="text-red-500 hover:text-red-700 font-medium flex-shrink-0"
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900" data-testid="platform-admin-memberships-title">Members</h2>
            <p className="text-sm text-gray-500">Invite staff and manage roles and department access.</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="platform-admin-invite-form">
          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="staff@company.com"
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-invite-email-input"
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-invite-role-select"
          >
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="org_admin">Org Admin</option>
          </select>
          <select
            value={inviteDept}
            onChange={(e) => setInviteDept(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-invite-department-select"
          >
            <option value="kitchen">Kitchen</option>
            <option value="bar">Bar</option>
            <option value="foh">FOH</option>
            <option value="hr">HR</option>
            <option value="finance">Finance</option>
            <option value="ops">Ops</option>
            <option value="admin">Admin</option>
          </select>
          <button
            onClick={sendInvite}
            className="bg-primary-500 text-white rounded-lg px-4 py-2 hover:bg-primary-600 transition"
            data-testid="platform-admin-invite-submit-button"
          >
            Invite
          </button>
        </div>

        <div className="mt-6 overflow-x-auto" data-testid="platform-admin-memberships-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">User</th>
                <th className="py-2">Roles</th>
                <th className="py-2">Departments</th>
                <th className="py-2">Status</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} className="border-b last:border-b-0">
                  <td className="py-2">
                    <div className="font-medium text-gray-900">{m.user?.name || 'User'}</div>
                    <div className="text-gray-500">{m.user?.email || m.user_id}</div>
                  </td>
                  <td className="py-2">
                    <span className="text-gray-700" data-testid={`membership-roles-${m.id}`}>{(m.roles || []).join(', ')}</span>
                  </td>
                  <td className="py-2">
                    <span className="text-gray-700" data-testid={`membership-depts-${m.id}`}>{(m.departments || []).join(', ') || 'All'}</span>
                  </td>
                  <td className="py-2">
                    <span className={`px-2 py-1 rounded text-xs ${m.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`membership-status-${m.id}`}>
                      {m.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="py-2">


                    <button
                      onClick={() => updateMembership(m.id, { is_active: !m.is_active })}
                      className="text-primary-600 hover:text-primary-700"
                      data-testid={`membership-toggle-${m.id}`}
                    >
                      {m.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
              {memberships.length === 0 && (
                <tr>
                  <td className="py-6 text-gray-500" colSpan={5} data-testid="platform-admin-memberships-empty">No members found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="platform-admin-modules-card">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900" data-testid="platform-admin-modules-title">Modules</h2>
            <p className="text-sm text-gray-500">
              {moduleConfig?.policies?.profile
                ? 'Features are set by your installer for this deployment; ask them to add or remove one.'
                : 'Feature flags for this organization.'}
            </p>
          </div>
          <button
            onClick={saveModuleConfig}
            disabled={saving || !moduleConfig || !!moduleConfig?.policies?.profile}
            className="bg-gray-900 text-white rounded-lg px-4 py-2 disabled:opacity-50"
            data-testid="platform-admin-modules-save-button"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="platform-admin-modules-grid">
          {modules.map((m) => (
            <label key={m.key} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50" data-testid={`module-toggle-${m.key}`}>
              <input
                type="checkbox"
                checked={!!(moduleConfig?.modules_enabled || {})[m.key]}
                disabled={!!moduleConfig?.policies?.profile}
                onChange={() => toggleModule(m.key)}
              />
              <span className="text-gray-800">{m.label}</span>
            </label>
          ))}
        </div>
      </div>

      {keysAllowed && (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="platform-admin-api-keys-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900" data-testid="platform-admin-api-keys-title">API Keys (Mobile/Devices)</h2>
            <p className="text-sm text-gray-500">
              Create an organization-scoped API key for mobile apps or devices. Use it as <code>Authorization: Bearer {'<API_KEY>'}</code>
              with <code>X-Organization-Id</code>.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="platform-admin-api-keys-create-form">
          <input
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            placeholder="Key name"
            data-testid="platform-admin-api-keys-name-input"
          />
          <select
            value={deviceIdForKey}
            onChange={(e) => setDeviceIdForKey(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-api-keys-device-select"
          >
            <option value="">(Optional) Link to Device</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name || d.device_type} — {d.identifier}</option>
            ))}
          </select>
          <button
            onClick={createApiKey}
            disabled={creatingKey || !activeOrgId}
            className="bg-gray-900 text-white rounded-lg px-4 py-2 disabled:opacity-50"
            data-testid="platform-admin-api-keys-create-button"
          >
            {creatingKey ? 'Creating…' : 'Create Key'}
          </button>
        </div>

        {createdKey?.api_key && (
          <div className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="platform-admin-api-keys-created-alert">
            <div className="text-sm text-amber-900 font-medium">Copy this key now — it will only be shown once.</div>
            <div className="mt-2 font-mono text-xs break-all bg-white border rounded p-2" data-testid="platform-admin-api-keys-created-value">{createdKey.api_key}</div>
            <div className="mt-2 text-xs text-amber-900" data-testid="platform-admin-api-keys-created-instructions">
              Use headers: Authorization: Bearer {'<API_KEY>'} and X-Organization-Id: {activeOrgId}
            </div>
          </div>
        )}

        <div className="mt-6 overflow-x-auto" data-testid="platform-admin-api-keys-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Name</th>
                <th className="py-2">Prefix</th>
                <th className="py-2">Org</th>
                <th className="py-2">Permissions</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys
                .filter(k => !k.organization_id || k.organization_id === activeOrgId)
                .map((k) => (
                  <tr key={k.id} className="border-b last:border-b-0">
                    <td className="py-2" data-testid={`api-key-name-${k.id}`}>{k.name}</td>
                    <td className="py-2 font-mono" data-testid={`api-key-prefix-${k.id}`}>{k.key_prefix}</td>
                    <td className="py-2" data-testid={`api-key-org-${k.id}`}>{k.organization_id || '—'}</td>
                    <td className="py-2" data-testid={`api-key-perms-${k.id}`}>{(k.permissions || []).join(', ')}</td>
                    <td className="py-2" data-testid={`api-key-status-${k.id}`}>{k.is_active ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))}
              {apiKeys.length === 0 && (
                <tr>
                  <td className="py-6 text-gray-500" colSpan={5} data-testid="platform-admin-api-keys-empty">No API keys found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="platform-admin-devices-card">
        <h2 className="text-lg font-semibold text-gray-900" data-testid="platform-admin-devices-title">Devices</h2>
        <p className="text-sm text-gray-500">Devices registered for this org/location.</p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="platform-admin-device-create-form">
          <input
            value={newDevice.name}
            onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
            className="px-3 py-2 border rounded-lg"
            placeholder="Device name (optional)"
            data-testid="platform-admin-device-name-input"
          />
          <select
            value={newDevice.device_type}
            onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value })}
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-device-type-select"
          >
            <option value="tablet">Tablet</option>
            <option value="kds_screen">KDS Screen</option>
            <option value="pos_terminal">POS Terminal</option>
            <option value="phone_line">Phone Line</option>
            <option value="whatsapp_number">WhatsApp Number</option>
            <option value="handheld">Handheld</option>
            <option value="other">Other</option>
          </select>
          <select
            value={newDevice.department}
            onChange={(e) => setNewDevice({ ...newDevice, department: e.target.value })}
            className="px-3 py-2 border rounded-lg"
            data-testid="platform-admin-device-department-select"
          >
            <option value="kitchen">Kitchen</option>
            <option value="bar">Bar</option>
            <option value="foh">FOH</option>
            <option value="hr">HR</option>
            <option value="finance">Finance</option>
            <option value="ops">Ops</option>
            <option value="admin">Admin</option>
          </select>
          <div className="flex gap-2">
            <input
              value={newDevice.identifier}
              onChange={(e) => setNewDevice({ ...newDevice, identifier: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
              placeholder="Identifier (phone/MAC/etc)"
              data-testid="platform-admin-device-identifier-input"
            />
            <button
              onClick={createDevice}
              disabled={creatingDevice || !newDevice.identifier}
              className="bg-primary-500 text-white rounded-lg px-4 py-2 disabled:opacity-50"
              data-testid="platform-admin-device-create-button"
            >
              {creatingDevice ? 'Creating…' : 'Add'}
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="platform-admin-devices-list">
          {devices.map((d) => (
            <div key={d.id} className="border rounded-lg p-3">
              <div className="font-medium text-gray-900" data-testid={`device-name-${d.id}`}>{d.name || d.device_type}</div>
              <div className="text-gray-500 text-sm" data-testid={`device-identifier-${d.id}`}>{d.identifier}</div>
              <div className="text-gray-500 text-sm" data-testid={`device-meta-${d.id}`}>Dept: {d.department || '—'}</div>
            </div>
          ))}
          {devices.length === 0 && (
            <div className="text-gray-500" data-testid="platform-admin-devices-empty">No devices found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Inventory Manager Component
const InventoryManager = ({ token, restaurants, platformHeaders, activeOrgId, activeLocationId, activeDepartment }) => {
  const [inventory, setInventory] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(restaurants[0]?.id || '');
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', category: 'produce', quantity: 0, unit: 'kg', cost_per_unit: 0, reorder_level: 10, supplier: ''
  });
  const [addItemError, setAddItemError] = useState(null);

  useEffect(() => {
    if (selectedRestaurant) fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant]);

  const fetchInventory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory?organization_id=${encodeURIComponent(activeOrgId)}&location_id=${encodeURIComponent(activeLocationId || '')}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        }
      });
      if (res.ok) setInventory(await res.json());
    } catch (err) {
      console.error('Error fetching inventory:', err);
    }
  };

  const addItem = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify({
          organization_id: activeOrgId,
          location_id: activeLocationId || null,
          department: activeDepartment || null,
          restaurant_id: selectedRestaurant || null,
          ...newItem
        })
      });
      if (res.ok) {
        setShowAddItem(false);
        setNewItem({ name: '', category: 'produce', quantity: 0, unit: 'kg', cost_per_unit: 0, reorder_level: 10, supplier: '' });
        setAddItemError(null);
        fetchInventory();
      } else {
        const err = await res.json().catch(() => ({}));
        setAddItemError(apiErrorMessage(err.detail, `Could not add item (${res.status})`));
      }
    } catch (err) {
      console.error('Error adding item:', err);
      setAddItemError('Network error — could not reach the server.');
    }
  };

  const categories = [
    { id: 'produce', name: 'Produce', icon: '🥬' },
    { id: 'protein', name: 'Protein', icon: '🍖' },
    { id: 'dairy', name: 'Dairy', icon: '🧀' },
    { id: 'dry-goods', name: 'Dry Goods', icon: '🌾' },
    { id: 'spices', name: 'Spices', icon: '🌶️' },
    { id: 'beverages', name: 'Beverages', icon: '🥤' }
  ];

  const lowStockItems = inventory.filter(item => item.quantity <= item.reorder_level);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Inventory Management</h2>
          <p className="text-gray-500 text-sm">Track stock levels and get AI-powered reorder recommendations</p>
        </div>
        <div className="flex gap-3">
          {restaurants.length > 0 && (
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowAddItem(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="add-inventory-btn"
          >
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Low Stock Alert</h3>
            <p className="text-sm text-amber-700">
              {lowStockItems.length} item{lowStockItems.length > 1 ? 's' : ''} need reordering: {lowStockItems.map(i => i.name).join(', ')}
            </p>
          </div>
        </div>
      )}

      {restaurants.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Add a restaurant first to manage inventory</p>
        </div>
      ) : (
        <>
          {/* Category Overview */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map(cat => {
              const catItems = inventory.filter(i => i.category === cat.id);
              const totalValue = catItems.reduce((sum, i) => sum + (i.quantity * i.cost_per_unit), 0);
              return (
                <div key={cat.id} className="bg-white rounded-xl p-4 text-center">
                  <div className="text-2xl mb-2">{cat.icon}</div>
                  <div className="font-medium text-gray-900">{cat.name}</div>
                  <div className="text-sm text-gray-500">{catItems.length} items</div>
                  <div className="text-xs text-primary-500 mt-1">£{totalValue.toFixed(0)} value</div>
                </div>
              );
            })}
          </div>

          {/* Inventory Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Item</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Quantity</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Unit Cost</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total Value</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        No inventory items yet. Add your first item to get started.
                      </td>
                    </tr>
                  ) : (
                    inventory.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          {item.supplier && <div className="text-xs text-gray-500">{item.supplier}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 capitalize">{item.category}</td>
                        <td className="px-4 py-3">
                          <span className="font-medium">{item.quantity}</span> {item.unit}
                        </td>
                        <td className="px-4 py-3 text-gray-600">£{item.cost_per_unit.toFixed(2)}</td>
                        <td className="px-4 py-3 font-medium">£{(item.quantity * item.cost_per_unit).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {item.quantity <= item.reorder_level ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                              <AlertTriangle className="w-3 h-3" /> Low Stock
                            </span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">In Stock</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button className="text-gray-400 hover:text-primary-500 transition">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-gray-400 hover:text-red-500 transition">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add Item Modal */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Add Inventory Item</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                <input aria-label="Item Name"
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Chicken Breast"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select aria-label="Category"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select aria-label="Unit"
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="kg">Kilograms (kg)</option>
                    <option value="g">Grams (g)</option>
                    <option value="l">Litres (L)</option>
                    <option value="ml">Millilitres (ml)</option>
                    <option value="units">Units</option>
                    <option value="packs">Packs</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input aria-label="Quantity"
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Unit (£)</label>
                  <input aria-label="Cost per Unit (£)"
                    type="number"
                    step="0.01"
                    value={newItem.cost_per_unit}
                    onChange={(e) => setNewItem({ ...newItem, cost_per_unit: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                  <input aria-label="Reorder Level"
                    type="number"
                    value={newItem.reorder_level}
                    onChange={(e) => setNewItem({ ...newItem, reorder_level: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <input aria-label="Supplier"
                    type="text"
                    value={newItem.supplier}
                    onChange={(e) => setNewItem({ ...newItem, supplier: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Metro"
                  />
                </div>
              </div>
            </div>
            {addItemError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="add-item-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{addItemError}</span>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddItem(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={addItem}
                className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Scheduling Manager Component
const SchedulingManager = ({ token, restaurants, platformHeaders, activeOrgId, activeLocationId, activeDepartment }) => {
  const [schedules, setSchedules] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(restaurants[0]?.id || '');
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    staff_name: '', role: 'server', shifts: []
  });
  const [addScheduleError, setAddScheduleError] = useState(null);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const roles = [
    { id: 'chef', name: 'Chef', icon: ChefHat },
    { id: 'server', name: 'Server', icon: UserCheck },
    { id: 'host', name: 'Host', icon: Users },
    { id: 'manager', name: 'Manager', icon: Building2 },
    { id: 'kitchen', name: 'Kitchen Staff', icon: Utensils }
  ];

  useEffect(() => {
    if (selectedRestaurant) fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant]);

  const fetchSchedules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/schedules?organization_id=${encodeURIComponent(activeOrgId)}&location_id=${encodeURIComponent(activeLocationId || '')}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        }
      });
      if (res.ok) setSchedules(await res.json());
    } catch (err) {
      console.error('Error fetching schedules:', err);
    }
  };

  const addSchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(platformHeaders || {})
        },
        body: JSON.stringify({
          organization_id: activeOrgId,
          location_id: activeLocationId || null,
          department: activeDepartment || null,
          restaurant_id: selectedRestaurant || null,
          ...newSchedule
        })
      });
      if (res.ok) {
        setShowAddSchedule(false);
        setNewSchedule({ staff_name: '', role: 'server', shifts: [] });
        setAddScheduleError(null);
        fetchSchedules();
      } else {
        const err = await res.json().catch(() => ({}));
        setAddScheduleError(apiErrorMessage(err.detail, `Could not add schedule (${res.status})`));
      }
    } catch (err) {
      console.error('Error adding schedule:', err);
      setAddScheduleError('Network error — could not reach the server.');
    }
  };

  const toggleShift = (day) => {
    const existing = newSchedule.shifts.find(s => s.day === day);
    if (existing) {
      setNewSchedule({
        ...newSchedule,
        shifts: newSchedule.shifts.filter(s => s.day !== day)
      });
    } else {
      setNewSchedule({
        ...newSchedule,
        shifts: [...newSchedule.shifts, { day, start_time: '09:00', end_time: '17:00' }]
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Staff Scheduling</h2>
          <p className="text-gray-500 text-sm">AI-optimized scheduling based on demand forecasts</p>
        </div>
        <div className="flex gap-3">
          {restaurants.length > 0 && (
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowAddSchedule(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="add-schedule-btn"
          >
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        </div>
      </div>

      {restaurants.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Add a restaurant first to manage schedules</p>
        </div>
      ) : (
        <>
          {/* Role Overview */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {roles.map(role => {
              const roleStaff = schedules.filter(s => s.role === role.id);
              return (
                <div key={role.id} className="bg-white rounded-xl p-4 text-center">
                  <role.icon className="w-8 h-8 text-primary-500 mx-auto mb-2" />
                  <div className="font-medium text-gray-900">{role.name}</div>
                  <div className="text-sm text-gray-500">{roleStaff.length} staff</div>
                </div>
              );
            })}
          </div>

          {/* Schedule Grid */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Weekly Schedule</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 sticky left-0 bg-gray-50">Staff</th>
                    {days.map(day => (
                      <th key={day} className="px-4 py-3 text-center text-sm font-medium text-gray-500 min-w-[100px]">
                        {day.slice(0, 3)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {schedules.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No staff schedules yet. Add your team members to create schedules.
                      </td>
                    </tr>
                  ) : (
                    schedules.map(schedule => (
                      <tr key={schedule.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 sticky left-0 bg-white">
                          <div className="font-medium text-gray-900">{schedule.staff_name}</div>
                          <div className="text-xs text-gray-500 capitalize">{schedule.role}</div>
                        </td>
                        {days.map(day => {
                          const shift = schedule.shifts?.find(s => s.day === day);
                          return (
                            <td key={day} className="px-4 py-3 text-center">
                              {shift ? (
                                <span className="inline-block bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded">
                                  {shift.start_time} - {shift.end_time}
                                </span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Recommendation */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">AI Scheduling Insight</h3>
                <p className="text-white/90 text-sm">
                  Based on historical data, Fridays and Saturdays need 40% more staff coverage between 7-10 PM.
                  Consider adding 2 additional servers during peak hours to reduce wait times and increase customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Schedule Modal */}
      {showAddSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Add Staff Member</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
                <input aria-label="Staff Name"
                  type="text"
                  value={newSchedule.staff_name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, staff_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select aria-label="Role"
                  value={newSchedule.role}
                  onChange={(e) => setNewSchedule({ ...newSchedule, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Working Days</label>
                <div className="flex flex-wrap gap-2">
                  {days.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleShift(day)}
                      className={`px-3 py-1 rounded-full text-sm transition ${
                        newSchedule.shifts.some(s => s.day === day)
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {addScheduleError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="add-schedule-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{addScheduleError}</span>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddSchedule(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={addSchedule}
                className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition"
              >
                Add Staff
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = ({ analyses }) => {
  const savingsData = analyses.slice(0, 10).map((a, idx) => ({
    name: `Analysis ${idx + 1}`,
    savings: a.results.total_annual_savings,
    roi: a.results.roi_percentage
  })).reverse();

  const initiativeTotals = analyses.reduce((acc, a) => {
    a.results.initiatives?.forEach(init => {
      if (!acc[init.name]) acc[init.name] = 0;
      acc[init.name] += init.savings;
    });
    return acc;
  }, {});

  const pieData = Object.entries(initiativeTotals).map(([name, value]) => ({
    name: name.split(' ').slice(0, 2).join(' '),
    value
  }));

  const COLORS = ['#f97316', '#ea580c', '#c2410c', '#9a3412', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Analytics Dashboard</h2>
        <p className="text-gray-500 text-sm">Insights from your cost analyses</p>
      </div>

      {analyses.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No data yet. Run cost analyses to see insights.</p>
          <Link to="/calculator" className="text-primary-500 hover:underline">
            Create Your First Analysis <ArrowRight className="w-4 h-4 inline" />
          </Link>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-500 text-sm">Total Analyses</p>
              <p className="text-3xl font-bold text-gray-900">{analyses.length}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-500 text-sm">Total Savings Identified</p>
              <p className="text-3xl font-bold text-primary-500">
                £{analyses.reduce((sum, a) => sum + a.results.total_annual_savings, 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-gray-500 text-sm">Average ROI</p>
              <p className="text-3xl font-bold text-green-600">
                {(analyses.reduce((sum, a) => sum + a.results.roi_percentage, 0) / analyses.length).toFixed(0)}%
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={savingsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                  <Bar dataKey="savings" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings by Initiative</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name }) => name}
                  >
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Main App
// Comparison Page
const ComparisonPage = () => {
  const [scenarios, setScenarios] = useState([
    { name: 'Current State', inputs: { pos_monthly_cost: 189, reservation_monthly_cost: 700, delivery_commission_rate: 28, monthly_delivery_revenue: 8000, phone_staff_hours_weekly: 4, hourly_wage: 15, inventory_manager_hours_weekly: 8, social_media_hours_weekly: 10, food_waste_percentage: 5, monthly_food_cost: 22000, staff_scheduling_hours_weekly: 4, payroll_monthly_cost: 500 }},
    { name: 'With AI Automation', inputs: { pos_monthly_cost: 100, reservation_monthly_cost: 139, delivery_commission_rate: 10, monthly_delivery_revenue: 12000, phone_staff_hours_weekly: 1, hourly_wage: 15, inventory_manager_hours_weekly: 2, social_media_hours_weekly: 2, food_waste_percentage: 2, monthly_food_cost: 22000, staff_scheduling_hours_weekly: 1, payroll_monthly_cost: 100 }}
  ]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const compareScenarios = async () => {
    if (scenarios.length < 2) {
      alert('Please add at least 2 scenarios to compare');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/comparisons/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarios })
      });
      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error('Comparison error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addScenario = () => {
    if (scenarios.length >= 5) {
      alert('Maximum 5 scenarios allowed');
      return;
    }
    setScenarios([...scenarios, { name: `Scenario ${scenarios.length + 1}`, inputs: { ...scenarios[0].inputs } }]);
  };

  const updateScenario = (index, field, value) => {
    const updated = [...scenarios];
    if (field === 'name') {
      updated[index].name = value;
    } else {
      updated[index].inputs[field] = parseFloat(value) || 0;
    }
    setScenarios(updated);
  };

  const removeScenario = (index) => {
    if (scenarios.length <= 2) {
      alert('Minimum 2 scenarios required');
      return;
    }
    setScenarios(scenarios.filter((_, i) => i !== index));
  };

  const COLORS = ['#f97316', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Scenario Comparison</h1>
          <p className="text-gray-600">Compare different operational scenarios side by side</p>
        </div>

        {/* Scenario Builder */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Build Your Scenarios</h2>
            <button
              onClick={addScenario}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
              data-testid="add-scenario-btn"
            >
              <Plus className="w-4 h-4" /> Add Scenario
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Metric</th>
                  {scenarios.map((scenario, idx) => (
                    <th key={idx} className="text-left py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }}></div>
                        <input
                          type="text"
                          value={scenario.name}
                          onChange={(e) => updateScenario(idx, 'name', e.target.value)}
                          className="font-medium border-b border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none"
                        />
                        {scenarios.length > 2 && (
                          <button onClick={() => removeScenario(idx)} className="text-gray-400 hover:text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'pos_monthly_cost', label: 'POS Monthly (£)' },
                  { key: 'reservation_monthly_cost', label: 'Reservation Monthly (£)' },
                  { key: 'delivery_commission_rate', label: 'Delivery Commission (%)' },
                  { key: 'monthly_delivery_revenue', label: 'Monthly Delivery Revenue (£)' },
                  { key: 'phone_staff_hours_weekly', label: 'Phone Staff Hours/Week' },
                  { key: 'inventory_manager_hours_weekly', label: 'Inventory Hours/Week' },
                  { key: 'social_media_hours_weekly', label: 'Social Media Hours/Week' },
                  { key: 'food_waste_percentage', label: 'Food Waste (%)' },
                  { key: 'monthly_food_cost', label: 'Monthly Food Cost (£)' }
                ].map((metric) => (
                  <tr key={metric.key} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-600">{metric.label}</td>
                    {scenarios.map((scenario, idx) => (
                      <td key={idx} className="py-3 px-4">
                        <input
                          type="number"
                          value={scenario.inputs[metric.key]}
                          onChange={(e) => updateScenario(idx, metric.key, e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={compareScenarios}
            disabled={loading}
            className="mt-6 w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition flex items-center justify-center"
            data-testid="compare-btn"
          >
            {loading ? <Spinner /> : <><GitCompare className="w-5 h-5 mr-2" /> Compare Scenarios</>}
          </button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
              <h3 className="text-xl font-semibold mb-4">Comparison Summary</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <p className="text-white/80 text-sm">Best for Savings</p>
                  <p className="text-2xl font-bold">{results.summary.best_savings}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Best ROI</p>
                  <p className="text-2xl font-bold">{results.summary.best_roi}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Savings Difference</p>
                  <p className="text-2xl font-bold">£{results.summary.savings_range.difference.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Comparison Chart */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Comparison</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={results.scenarios.map(s => ({ name: s.name, savings: s.results.total_annual_savings, roi: s.results.roi_percentage }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, name) => name === 'savings' ? `£${v.toLocaleString()}` : `${v}%`} />
                  <Bar yAxisId="left" dataKey="savings" fill="#f97316" name="Annual Savings" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="roi" fill="#10b981" name="ROI %" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Detailed Comparison */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Metric</th>
                      {results.scenarios.map((s, idx) => (
                        <th key={idx} className="text-right py-3 px-4 font-medium" style={{ color: COLORS[idx] }}>{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-4 text-gray-600">Annual Savings</td>
                      {results.scenarios.map((s, idx) => (
                        <td key={idx} className="py-3 px-4 text-right font-semibold">£{s.results.total_annual_savings.toLocaleString()}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 text-gray-600">ROI</td>
                      {results.scenarios.map((s, idx) => (
                        <td key={idx} className="py-3 px-4 text-right font-semibold">{s.results.roi_percentage}%</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 text-gray-600">Payback Period</td>
                      {results.scenarios.map((s, idx) => (
                        <td key={idx} className="py-3 px-4 text-right">{s.results.payback_months} months</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 text-gray-600">Current Costs</td>
                      {results.scenarios.map((s, idx) => (
                        <td key={idx} className="py-3 px-4 text-right">£{s.results.current_annual_costs.toLocaleString()}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">Future Costs</td>
                      {results.scenarios.map((s, idx) => (
                        <td key={idx} className="py-3 px-4 text-right">£{s.results.future_annual_costs.toLocaleString()}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Proposals Page (Protected)
const ProposalsPage = () => {
  const { token } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newProposal, setNewProposal] = useState({
    client_name: '', client_email: '', client_company: '', proposal_type: 'discovery', custom_message: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProposals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/proposals`, {
        headers: authHeaders({ token })
      });
      if (res.ok) setProposals(await res.json());
    } catch (err) {
      console.error('Error fetching proposals:', err);
    } finally {
      setLoading(false);
    }
  };

  const createProposal = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/proposals/generate`, {
        method: 'POST',
        headers: authHeaders({ token, json: true }),
        body: JSON.stringify(newProposal)
      });
      if (res.ok) {
        setShowCreate(false);
        setNewProposal({ client_name: '', client_email: '', client_company: '', proposal_type: 'discovery', custom_message: '' });
        fetchProposals();
      }
    } catch (err) {
      console.error('Error creating proposal:', err);
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (proposalId) => {
    const response = await fetch(`${API_URL}/api/proposals/${proposalId}/pdf`, {
      headers: authHeaders({ token })
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposal_${proposalId.slice(0, 8)}.pdf`;
    a.click();
  };

  const sendEmail = async (proposal) => {
    try {
      const res = await fetch(`${API_URL}/api/email/send-report`, {
        method: 'POST',
        headers: authHeaders({ token, json: true }),
        body: JSON.stringify({
          to_email: proposal.client_email,
          to_name: proposal.client_name,
          subject: 'Discovery Engagement Proposal - FoodIndustry AI',
          content_type: 'proposal',
          content_id: proposal.id
        })
      });
      if (res.ok) {
        alert('Proposal sent successfully!');
        fetchProposals();
      }
    } catch (err) {
      console.error('Error sending email:', err);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Spinner /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Proposals</h1>
            <p className="text-gray-600">Create and manage client proposals</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="create-proposal-btn"
          >
            <Plus className="w-5 h-5" /> New Proposal
          </button>
        </div>

        {/* Proposals List */}
        {proposals.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No proposals yet</p>
            <button onClick={() => setShowCreate(true)} className="text-primary-500 hover:underline">
              Create your first proposal
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {proposals.map(proposal => (
              <div key={proposal.id} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{proposal.client_company}</h3>
                    <p className="text-gray-600">{proposal.client_name} • {proposal.client_email}</p>
                    <p className="text-sm text-gray-500 mt-1">Created: {new Date(proposal.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      proposal.status === 'sent' ? 'bg-green-100 text-green-700' :
                      proposal.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {proposal.status}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => downloadPdf(proposal.id)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-500 transition"
                  >
                    <Download className="w-4 h-4" /> Download PDF
                  </button>
                  <button
                    onClick={() => sendEmail(proposal)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-500 transition"
                  >
                    <Mail className="w-4 h-4" /> Send to Client
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-semibold mb-4">Create Proposal</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <input aria-label="Client Name"
                    type="text"
                    value={newProposal.client_name}
                    onChange={(e) => setNewProposal({ ...newProposal, client_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Swati Varanasi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                  <input aria-label="Client Email"
                    type="email"
                    value={newProposal.client_email}
                    onChange={(e) => setNewProposal({ ...newProposal, client_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., swati@restaurant.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company/Restaurant</label>
                  <input aria-label="Company/Restaurant"
                    type="text"
                    value={newProposal.client_company}
                    onChange={(e) => setNewProposal({ ...newProposal, client_company: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Curry on Naanstop"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Type</label>
                  <select aria-label="Proposal Type"
                    value={newProposal.proposal_type}
                    onChange={(e) => setNewProposal({ ...newProposal, proposal_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="discovery">Discovery Engagement (£12,500)</option>
                    <option value="implementation">Full Implementation</option>
                    <option value="custom">Custom Solution</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Message (Optional)</label>
                  <textarea aria-label="Custom Message (Optional)"
                    value={newProposal.custom_message}
                    onChange={(e) => setNewProposal({ ...newProposal, custom_message: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Add a personalized message..."
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={createProposal}
                  disabled={creating || !newProposal.client_name || !newProposal.client_email}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
                >
                  {creating ? <Spinner /> : 'Create Proposal'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Implementation Tracking Page (Phase 2)
const ImplementationPage = () => {
  const { token } = useAuth();
  const [implementations, setImplementations] = useState([]);
  const [selectedImpl, setSelectedImpl] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [restaurants, setRestaurants] = useState([]);

  const [platformCtx, setPlatformCtx] = useState(null);
  const [activeOrgId, setActiveOrgId] = useState('');
  const [activeLocationId, setActiveLocationId] = useState('');
  const [activeDepartment, setActiveDepartment] = useState(readActiveDepartment);

  const [newImpl, setNewImpl] = useState({ restaurant_id: '', name: '', target_savings: 157000 });

  useEffect(() => {
    initAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initAndFetch = async () => {
    try {
      setLoading(true);
      const baseHeaders = { Authorization: `Bearer ${token}` };

      const ctxRes = await fetch(`${API_URL}/api/platform/context`, { headers: baseHeaders });
      const ctxJson = await ctxRes.json();
      setPlatformCtx(ctxJson);

      const defaultOrgId = ctxJson?.organizations?.[0]?.id || '';
      const defaultLocId = ctxJson?.organizations?.[0]?.locations?.[0]?.id || '';

      const orgId = activeOrgId || defaultOrgId;
      const locId = activeLocationId || defaultLocId;

      setActiveOrgId(orgId);
      setActiveLocationId(locId);

      await fetchData(orgId, locId, activeDepartment);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  const fetchData = async (orgId, locId, dept) => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': orgId,
        ...(locId ? { 'X-Location-Id': locId } : {}),
        ...(dept ? { 'X-Department': dept } : {})
      };
      const [implRes, restRes] = await Promise.all([
        fetch(`${API_URL}/api/implementations`, { headers }),
        fetch(`${API_URL}/api/restaurants`, { headers })
      ]);

      const impls = await implRes.json();
      const rests = await restRes.json();

      setImplementations(impls);
      setRestaurants(rests);

      if (impls.length > 0 && !selectedImpl) {
        setSelectedImpl(impls[0]);
        fetchDashboard(impls[0].id, headers);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async (implId, overrideHeaders) => {
    try {
      const headers = overrideHeaders || {
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': activeOrgId,
        ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
        ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
      };

      const res = await fetch(`${API_URL}/api/implementations/${implId}/dashboard`, { headers });
      if (res.ok) setDashboard(await res.json());
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const createImplementation = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': activeOrgId,
        ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
        ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
      };

      const res = await fetch(`${API_URL}/api/implementations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organization_id: activeOrgId,
          ...newImpl
        })
      });
      if (res.ok) {
        setShowCreate(false);
        setNewImpl({ restaurant_id: '', name: '', target_savings: 157000 });
        fetchData(activeOrgId, activeLocationId, activeDepartment);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const updateInitiative = async (initId, status, progress, actualSavings) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': activeOrgId,
        ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
        ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
      };

      await fetch(`${API_URL}/api/implementations/${selectedImpl.id}/initiatives/${initId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status, progress_percentage: progress, actual_savings: actualSavings })
      });

      // Refresh data
      const res = await fetch(`${API_URL}/api/implementations/${selectedImpl.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-Id': activeOrgId,
          ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
          ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
        }
      });
      if (res.ok) {
        const updated = await res.json();
        setSelectedImpl(updated);
        fetchDashboard(selectedImpl.id);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 1: return 'bg-primary-100 text-primary-700';
      case 2: return 'bg-purple-100 text-purple-700';
      case 3: return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Spinner /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Implementation Tracker</h1>
            <p className="text-gray-600">Track progress on your AI transformation initiatives</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-600 transition"
            data-testid="create-implementation-btn"
          >
            <Plus className="w-5 h-5" /> New Implementation
          </button>
        </div>

        {/* Platform Context Selector */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 mb-6" data-testid="implementation-platform-context-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select aria-label="Organization"
                value={activeOrgId}
                onChange={async (e) => {
                  const nextOrgId = e.target.value;
                  setActiveOrgId(nextOrgId);
                  const nextOrg = platformCtx?.organizations?.find(o => o.id === nextOrgId);
                  const nextLocId = nextOrg?.locations?.[0]?.id || '';
                  setActiveLocationId(nextLocId);
                  await fetchData(nextOrgId, nextLocId, activeDepartment);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="implementation-org-selector"
              >
                {(platformCtx?.organizations || []).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select aria-label="Location"
                value={activeLocationId}
                onChange={async (e) => {
                  const nextLocId = e.target.value;
                  setActiveLocationId(nextLocId);
                  await fetchData(activeOrgId, nextLocId, activeDepartment);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="implementation-location-selector"
              >
                {((platformCtx?.organizations || []).find(o => o.id === activeOrgId)?.locations || []).map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select aria-label="Department"
                value={activeDepartment}
                onChange={async (e) => {
                  const nextDept = e.target.value;
                  setActiveDepartment(nextDept);
                  broadcastActiveDepartment(nextDept);
                  await fetchData(activeOrgId, activeLocationId, nextDept);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                data-testid="implementation-department-selector"
              >
                <option value="">All</option>
                <option value="kitchen">Kitchen</option>
                <option value="bar">Bar</option>
                <option value="foh">FOH</option>
                <option value="hr">HR</option>
                <option value="finance">Finance</option>
                <option value="ops">Ops</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3" data-testid="implementation-platform-context-hint">
            Scope the implementation tracker by organization/location/department.
          </p>
        </div>


        {implementations.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Implementation Projects</h3>
            <p className="text-gray-500 mb-4">Start tracking your restaurant&apos;s AI transformation journey</p>
            <button onClick={() => setShowCreate(true)} className="bg-primary-500 text-white px-6 py-2 rounded-lg">
              Create Implementation
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar - Implementation List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Projects</h3>
                <div className="space-y-2">
                  {implementations.map(impl => (
                    <button
                      key={impl.id}
                      onClick={() => {
                        const headers = {
                          Authorization: `Bearer ${token}`,
                          'X-Organization-Id': activeOrgId,
                          ...(activeLocationId ? { 'X-Location-Id': activeLocationId } : {}),
                          ...(activeDepartment ? { 'X-Department': activeDepartment } : {})
                        };
                        setSelectedImpl(impl);
                        fetchDashboard(impl.id, headers);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition ${
                        selectedImpl?.id === impl.id ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900 truncate">{impl.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">{impl.overall_progress}% complete</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(impl.status)}`}>
                          {impl.status}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {selectedImpl && dashboard && (
                <>
                  {/* Dashboard Cards */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-500 text-sm">Overall Progress</p>
                      <p className="text-2xl font-bold text-gray-900">{dashboard.implementation.overall_progress}%</p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-primary-500 rounded-full h-2 transition-all" 
                          style={{ width: `${dashboard.implementation.overall_progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-500 text-sm">Target Savings</p>
                      <p className="text-2xl font-bold text-gray-900">£{dashboard.savings.target.toLocaleString()}</p>
                    </div>
                    <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 shadow-sm text-white">
                      <p className="text-white/80 text-sm">Actual Savings</p>
                      <p className="text-2xl font-bold">£{dashboard.savings.actual_total.toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-500 text-sm">Achievement</p>
                      <p className="text-2xl font-bold text-primary-500">{dashboard.savings.achievement_rate}%</p>
                    </div>
                  </div>

                  {/* Phase Progress */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Phase Progress</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {[1, 2, 3].map(phase => {
                        const phaseData = dashboard.initiatives.by_phase[`phase_${phase}`];
                        const progress = phaseData.count > 0 ? (phaseData.completed / phaseData.count) * 100 : 0;
                        return (
                          <div key={phase} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPhaseColor(phase)}`}>
                                Phase {phase}
                              </span>
                              <span className="text-sm text-gray-500">{phaseData.completed}/{phaseData.count}</span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              {phase === 1 ? 'Quick Wins' : phase === 2 ? 'Core Operations' : 'Tech Transformation'}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`rounded-full h-2 ${phase === 1 ? 'bg-primary-500' : phase === 2 ? 'bg-purple-500' : 'bg-indigo-500'}`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Initiatives List */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Initiatives</h3>
                    <div className="space-y-3">
                      {selectedImpl.initiatives?.map(init => (
                        <div key={init.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPhaseColor(init.phase)}`}>
                                  Phase {init.phase}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(init.status)}`}>
                                  {init.status.replace('_', ' ')}
                                </span>
                              </div>
                              <h4 className="font-medium text-gray-900">{init.name}</h4>
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                <span>Est: £{init.estimated_savings.toLocaleString()}/yr</span>
                                <span>Actual: £{(init.actual_savings || 0).toLocaleString()}</span>
                                <span>Payback: {init.payback_weeks} weeks</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={init.status}
                                onChange={(e) => updateInitiative(init.id, e.target.value, init.progress_percentage, init.actual_savings)}
                                className="text-sm border rounded-lg px-2 py-1"
                              >
                                <option value="not_started">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="blocked">Blocked</option>
                              </select>
                            </div>
                          </div>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                              <span>Progress</span>
                              <span>{init.progress_percentage}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={init.progress_percentage}
                              onChange={(e) => updateInitiative(init.id, init.status, parseInt(e.target.value), init.actual_savings)}
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>
                          {init.notes && (
                            <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">{init.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="implementation-create-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-semibold mb-4">Create Implementation Project</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant</label>
                  <select aria-label="Restaurant"
                    value={newImpl.restaurant_id}
                    onChange={(e) => setNewImpl({ ...newImpl, restaurant_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    data-testid="implementation-create-restaurant-select"
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                  <input aria-label="Project Name"
                    type="text"
                    value={newImpl.name}
                    onChange={(e) => setNewImpl({ ...newImpl, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., AI Transformation 2025"
                    data-testid="implementation-create-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Annual Savings (£)</label>
                  <input aria-label="Target Annual Savings (£)"
                    type="number"
                    value={newImpl.target_savings}
                    onChange={(e) => setNewImpl({ ...newImpl, target_savings: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    data-testid="implementation-create-target-savings-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCreate(false)} className="flex-1 border py-2 rounded-lg" data-testid="implementation-create-cancel-button">Cancel</button>
                <button
                  onClick={createImplementation}
                  disabled={!newImpl.restaurant_id}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg disabled:opacity-50"
                  data-testid="implementation-create-submit-button"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Bind device work to the authenticated actor and the selected organization/site.
function ReceivingCapturePage() {
  const { user, token } = useAuth();
  const scope = useDeviceScope(user?.id, token);
  return <CapturePage key={scopeKey(scope)} apiUrl={API_URL} token={scope.token}
    userId={scope.userId} organizationId={scope.orgId} locationId={scope.locationId} />;
}

function ScopedScanStationPage() {
  const { user, token } = useAuth();
  const scope = useDeviceScope(user?.id, token);
  return <ScanStationPage key={scopeKey(scope)} scope={scope} />;
}

function ReceivingSettingsPage() {
  const { user } = useAuth();
  return <TemperatureSettings userId={user?.id} />;
}

function ReceivingQueuePage() {
  const { user } = useAuth();
  return <InboundQueue userId={user?.id} />;
}

function ReceivingInboxPage() {
  const { user } = useAuth();
  return <OperatorInbox userId={user?.id} />;
}

function ReceivingReceiptPage() {
  const { user } = useAuth();
  const { receiptId } = useParams();
  return <ReceiptWorkspace userId={user?.id} receiptId={receiptId} />;
}

function ReceivingReviewPage() {
  const { user } = useAuth();
  const { receiptId } = useParams();
  return <ManagerReview userId={user?.id} receiptId={receiptId} />;
}

function ReceivingPutawayPage() {
  const { user } = useAuth();
  return <Putaway userId={user?.id} />;
}

function ReceivingClaimsPage() {
  const { user } = useAuth();
  const { claimId } = useParams();
  return <Claims userId={user?.id} claimId={claimId} />;
}

function ReceivingSyncPage() {
  const { user } = useAuth();
  return <SyncCenter userId={user?.id} />;
}

function App() {
  // demo-review-2026-09-09 P0: MobileApp is reachable only by an explicit
  // /mobile URL, not by UA-sniffing or PWA display-mode — every mobile
  // screen currently throws (getIdToken is not a function), so
  // auto-routing every phone visitor there with no way back to desktop
  // meant a prospect who opened the demo link on a phone got a dead end.
  // See mobileRouting.js.
  const nativeStart = nativeStartPath(window.location.pathname);
  if (nativeStart) window.history.replaceState(null, '', nativeStart);
  if (shouldShowMobileApp({ pathname: window.location.pathname })) {
    return <MobileApp />;
  }

  if (window.location.pathname.startsWith('/contractor/job/')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/contractor/job/:jobId" element={<ContractorJobPortal />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthedExperience>
        <div className="min-h-screen bg-gray-50">
          <Navbar />
          <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <GuardedScreens>
            <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/calculator" element={<CalculatorPage />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/demos" element={<DemosPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInviteRoute />} />
            <Route path="/trial" element={<TrialSignup />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/first-run" element={<ProtectedRoute><FirstRunWizard /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute><MembershipPage /></ProtectedRoute>} />
            <Route path="/proposals" element={<ProtectedRoute><ProposalsPage /></ProtectedRoute>} />
            <Route path="/implementations" element={<ProtectedRoute><ImplementationPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventsListPage /></ProtectedRoute>} />
            <Route path="/events/:eventId" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
            {surfaceEnabled('room-booking') && <Route path="/room-booking" element={<ProtectedRoute><RoomBooking /></ProtectedRoute>} />}
            <Route path="/inventory" element={<ProtectedRoute><InventoryManagement /></ProtectedRoute>} />
            <Route path="/ck-ops" element={<ProtectedRoute><CkOperations /></ProtectedRoute>} />
            <Route path="/ck-ops/billing" element={<ProtectedRoute><CkOperations initialTab="billing" /></ProtectedRoute>} />
            <Route path="/ck-ops/price-lists" element={<ProtectedRoute><CkOperations initialTab="price-lists" /></ProtectedRoute>} />
            <Route path="/settings/destinations" element={<ProtectedRoute><CkOperations initialTab="destinations" /></ProtectedRoute>} />
            <Route path="/scan" element={<ProtectedRoute><ScopedScanStationPage /></ProtectedRoute>} />
            <Route path="/process-discovery" element={<ProtectedRoute><OperandiHumanContext /></ProtectedRoute>} />
            <Route path="/receiving/settings" element={<ProtectedRoute><ReceivingSettingsPage /></ProtectedRoute>} />
            <Route path="/receiving/capture" element={<ProtectedRoute><ReceivingCapturePage /></ProtectedRoute>} />
            <Route path="/scan/gates" element={<ProtectedRoute><ScanGatesPage /></ProtectedRoute>} />
            <Route path="/scan/lots/:lotId" element={<ProtectedRoute><LotLineagePage /></ProtectedRoute>} />
            <Route path="/m/today" element={<ProtectedRoute><ReceivingQueuePage /></ProtectedRoute>} />
            <Route path="/m/inbox" element={<ProtectedRoute><ReceivingInboxPage /></ProtectedRoute>} />
            <Route path="/m/receipt/:receiptId" element={<ProtectedRoute><ReceivingReceiptPage /></ProtectedRoute>} />
            <Route path="/receiving/reviews" element={<ProtectedRoute><ReceivingReviewPage /></ProtectedRoute>} />
            <Route path="/receiving/reviews/:receiptId" element={<ProtectedRoute><ReceivingReviewPage /></ProtectedRoute>} />
            <Route path="/receiving/putaway" element={<ProtectedRoute><ReceivingPutawayPage /></ProtectedRoute>} />
            <Route path="/receiving/claims" element={<ProtectedRoute><ReceivingClaimsPage /></ProtectedRoute>} />
            <Route path="/receiving/claims/:claimId" element={<ProtectedRoute><ReceivingClaimsPage /></ProtectedRoute>} />
            <Route path="/group" element={<ProtectedRoute><GroupView /></ProtectedRoute>} />
            <Route path="/owner/queue" element={<ProtectedRoute><OwnerQueue /></ProtectedRoute>} />
            <Route path="/controls/replenishment" element={<ProtectedRoute><ReplenishmentProposals /></ProtectedRoute>} />
            <Route path="/safety/missed-checks" element={<ProtectedRoute><MissedChecks /></ProtectedRoute>} />
            <Route path="/m/sync" element={<ProtectedRoute><ReceivingSyncPage /></ProtectedRoute>} />
            <Route path="/waste" element={<ProtectedRoute><Waste /></ProtectedRoute>} />
            <Route path="/decisions" element={<ProtectedRoute><DecisionsReview /></ProtectedRoute>} />
            <Route path="/marketplace" element={<ProtectedRoute><MarketplaceBuyer /></ProtectedRoute>} />
            <Route path="/marketplace-ops" element={<ProtectedRoute><MarketplaceOperator /></ProtectedRoute>} />
            <Route path="/controls" element={<ProtectedRoute><Controls /></ProtectedRoute>} />
            <Route path="/workforce" element={<ProtectedRoute><Workforce /></ProtectedRoute>} />
            <Route path="/contractor-jobs" element={<ProtectedRoute><ContractorJobs /></ProtectedRoute>} />
            <Route path="/onboarding/persona" element={<ProtectedRoute><PersonaOnboarding /></ProtectedRoute>} />
            <Route path="/demand-forecast" element={<ProtectedRoute><DemandForecast /></ProtectedRoute>} />
            {surfaceEnabled('delivery') && <Route path="/delivery" element={<ProtectedRoute><DeliveryManagement /></ProtectedRoute>} />}
            {surfaceEnabled('safety') && <Route path="/safety" element={<ProtectedRoute><SafetyHealth /></ProtectedRoute>} />}
            <Route path="/visual-safety" element={<ProtectedRoute><VisualSafety /></ProtectedRoute>} />
            {surfaceEnabled('enhanced-events') && <Route path="/enhanced-events" element={<ProtectedRoute><EnhancedEventPlanning /></ProtectedRoute>} />}
            <Route path="/directconnect" element={<ProtectedRoute><DirectConnect /></ProtectedRoute>} />
            <Route path="/roi" element={<ProtectedRoute><ROIDashboard /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/menu-config" element={<ProtectedRoute><MenuConfiguration /></ProtectedRoute>} />
            <Route path="/menu-items" element={<ProtectedRoute><MenuItemManagement /></ProtectedRoute>} />
            <Route path="/n8n-config" element={<ProtectedRoute><N8NConfiguration /></ProtectedRoute>} />
            <Route path="/pos/order-entry" element={<ProtectedRoute><PosOrderEntry /></ProtectedRoute>} />
            <Route path="/pos/tables" element={<ProtectedRoute><TableManagement /></ProtectedRoute>} />
            {/* Phase C — Cost Intelligence */}
            <Route path="/cost-intel" element={<ProtectedRoute><CostIntelligence /></ProtectedRoute>} />
            <Route path="/pricing/impact" element={<ProtectedRoute><PriceImpactPage /></ProtectedRoute>} />
            <Route path="/review" element={<ProtectedRoute><ReviewQueue /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
            <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
            <Route path="/chef-confirm" element={<ProtectedRoute><ChefConfirm /></ProtectedRoute>} />
            <Route path="/admin/access" element={<ProtectedRoute><AccessMatrix /></ProtectedRoute>} />
            <Route path="/recipes" element={<ProtectedRoute><RecipeManager /></ProtectedRoute>} />
            {/* Read-only PPDS (Natasha's Law) label data. No writes, no printing. */}
            <Route path="/recipes/ppds-label" element={<ProtectedRoute><PpdsLabel /></ProtectedRoute>} />
            <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
            {/* Phase 2.4 — Secure Document Vault */}
            <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
            <Route path="/finance/xero" element={<ProtectedRoute><Xero /></ProtectedRoute>} />
            <Route path="/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
            <Route path="/hrm" element={<ProtectedRoute><HRM /></ProtectedRoute>} />
            <Route path="/supplier-price-lists" element={<ProtectedRoute><SupplierPriceLists /></ProtectedRoute>} />
            <Route path="/procurement" element={<ProtectedRoute><Procurement /></ProtectedRoute>} />
            <Route path="/forecast" element={<ProtectedRoute><Forecast /></ProtectedRoute>} />
            <Route path="/data-room" element={<ProtectedRoute><DataRoom /></ProtectedRoute>} />
            <Route path="/voice" element={<ProtectedRoute><VoiceConcierge /></ProtectedRoute>} />
            <Route path="/settings/integrations" element={<ProtectedRoute><IntegrationsSettings /></ProtectedRoute>} />
            <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsRoute /></ProtectedRoute>} />
            <Route path="/settings/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
            <Route path="/settings/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
            <Route path="/settings/imports" element={<ProtectedRoute><ImportDataPage /></ProtectedRoute>} />
            <Route path="/settings/ai" element={<ProtectedRoute><AiSettingsPage /></ProtectedRoute>} />
            <Route path="/settings/integrations/deputy" element={<ProtectedRoute><DeputySettingsRedirect /></ProtectedRoute>} />
            {/* Phase D — Alerting cockpit */}
            <Route path="/alerts" element={<ProtectedRoute><AlertsCockpit /></ProtectedRoute>} />
            {/* Phase H — KDS (tablet-mounted, no chrome) */}
            <Route path="/pos/kds" element={<ProtectedRoute><KdsView /></ProtectedRoute>} />
            {/* Phase H follow-up — Cashier close-of-day */}
            <Route path="/pos/close" element={<ProtectedRoute><CashierClose /></ProtectedRoute>} />
            {/* Phase I — Public guest RSVP (no auth) */}
            <Route path="/guest/rsvp/:token" element={<GuestRSVP />} />
            {/* Phase I follow-up — Public room booking (no auth) */}
            <Route path="/guest/book/:orgId" element={<GuestRoomBooking />} />
            {/* Phase J — Superadmin cross-tenant console */}
            <Route path="/admin/superadmin" element={<ProtectedRoute><SuperAdminConsole /></ProtectedRoute>} />
            {/* Catch-all. Without this, an unknown URL — a typo, a stale
                bookmark, or a surface hidden behind surfaceEnabled() — rendered
                a blank ~80-char page indistinguishable from an outage. */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </GuardedScreens>
          </Suspense>
          </RouteErrorBoundary>
          <AuthedFeedback />
        </div>
        </AuthedExperience>
      </AuthProvider>
    </BrowserRouter>
  );
}

// The experience (menus, landing, guarded screens) for the signed-in token; re-fetched when the token changes.
function AuthedExperience({ children }) {
  const { token } = useAuth();
  return <ExperienceProvider token={token}>{children}</ExperienceProvider>;
}

// A screen whose module is off for the organisation shows "not enabled" instead of opening by URL.
const GUARDED_MENU_SCREENS = [...MENU_SCREENS, ...WORKSPACES.flatMap((ws) => ws.items)];
function GuardedScreens({ children }) {
  const { user } = useAuth();
  return (
    <ExperienceGuard menuItems={GUARDED_MENU_SCREENS} signedIn={!!user}>
      {user && <LicenceBanner />}
      {children}
    </ExperienceGuard>
  );
}

// Does this server let anyone sign themselves up? undefined until the server answers; an unreachable server counts as
// yes (the public page it always showed). An installed client server has sign-up off: people are invited by the owner.
function useSelfSignup() {
  const [open, setOpen] = useState(undefined);
  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/auth/options`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => { if (live) setOpen(!(o && o.self_signup === false)); })
      .catch(() => { if (live) setOpen(true); });
    return () => { live = false; };
  }, []);
  return open;
}

// "/": the public page for visitors; a signed-in person goes to where they start, not back to the sales pitch.
// On an installed client server (sign-up off), or under a client flavour (landing.visitor: signin), a visitor has
// nothing to read there: they get the sign-in form.
function HomeRoute() {
  const { user, loading } = useAuth();
  const exp = useExperience();
  const signup = useSelfSignup();
  const { landing } = useBrand();
  if (!user && !loading) {
    if (signup === undefined) return <RouteFallback />;
    // The vendor's public page only where anyone may sign up and the flavour wants it; a client's server signs in.
    return signup && landing.visitor !== 'signin' ? <LandingPage /> : <AuthPage />;
  }
  if (loading || exp.status === 'loading' || exp.status === 'anonymous') return <RouteFallback />;
  return <Navigate to={landingFor(exp, GUARDED_MENU_SCREENS, POST_LOGIN_ROUTE)} replace />;
}

export default App;
